import React, { useMemo, useRef, useState, useEffect } from "react";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Link } from "react-router-dom";
import API_BASE_URL from '../config';
import bannerImg from '../assets/Banners/700x200.webp'
import bannerImg2 from '../assets/Banners/700x200_1.webp'
import BrandSlider from "../components/BrandSlider.jsx";
import ProductCard from "../components/ProductCard";
import { getProductById, searchProducts, getFilteredProducts, getBrandsWithImages } from '../services/productService';
import filterService from '../services/filterService';
import { addItem as addCartItem } from '../services/cartService';
import { getCategories } from '../services/categoryService';
import { getPostCategories } from '../services/blogService';
import toast from 'react-hot-toast';


const serverBase = API_BASE_URL.replace(/\/api\/?$/i, '');

const ensureHttpsIfNeeded = (u) => {
    if (!u) return u;
    if (typeof u !== 'string') return u;
    try {
        if (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:' && u.startsWith('http://')) {
            return 'https://' + u.slice('http://'.length);
        }
    } catch (_) { }
    return u;
};

const resolveAssetUrl = (u) => {
    if (!u) return u;
    if (u.startsWith('http://') || u.startsWith('https://')) return ensureHttpsIfNeeded(u);
    if (u.startsWith('//')) return window.location.protocol + u;
    return u.startsWith('/') ? serverBase + u : serverBase + '/' + u;
};

/* Helper Components */

function SliderModule({ slides }) {
    const [idx, setIdx] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const count = slides ? slides.length : 0;
    const pauseTimerRef = useRef(null);
    const dragStartXRef = useRef(0);
    const dragStartYRef = useRef(0);
    const isDraggingRef = useRef(false);
    const hasMovedRef = useRef(false);

    const pauseAutoScrollTemporarily = () => {
        setIsPaused(true);
        if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
        pauseTimerRef.current = setTimeout(() => {
            setIsPaused(false);
        }, 6000);
    };

    useEffect(() => {
        if (isPaused || count <= 1) return;
        const interval = setInterval(() => {
            setIdx(prev => (prev + 1) % count);
        }, 5000);
        return () => clearInterval(interval);
    }, [isPaused, count]);

    if (!slides || slides.length === 0) return null;

    const handlePrev = (e) => {
        e?.stopPropagation();
        pauseAutoScrollTemporarily();
        setIdx(i => (i - 1 + count) % count);
    };

    const handleNext = (e) => {
        e?.stopPropagation();
        pauseAutoScrollTemporarily();
        setIdx(i => (i + 1) % count);
    };

    const onPointerDown = (e) => {
        if (count <= 1) return;
        const clientX = e.clientX ?? (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
        const clientY = e.clientY ?? (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
        dragStartXRef.current = clientX;
        dragStartYRef.current = clientY;
        isDraggingRef.current = true;
        hasMovedRef.current = false;
    };

    const onPointerMove = (e) => {
        if (!isDraggingRef.current) return;
        const clientX = e.clientX ?? (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
        const clientY = e.clientY ?? (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
        const diffX = clientX - dragStartXRef.current;
        const diffY = clientY - dragStartYRef.current;
        if (Math.abs(diffX) > 8 || Math.abs(diffY) > 8) {
            hasMovedRef.current = true;
        }
    };

    const onPointerUp = (e) => {
        if (!isDraggingRef.current) return;
        isDraggingRef.current = false;
        const endX = e.clientX ?? (e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : dragStartXRef.current);
        const diffX = endX - dragStartXRef.current;
        if (Math.abs(diffX) >= 40) {
            pauseAutoScrollTemporarily();
            if (diffX < 0) {
                setIdx(i => (i + 1) % count);
            } else {
                setIdx(i => (i - 1 + count) % count);
            }
        }
    };

    const Wrap = ({ link, children }) => {
        if (!link) return <>{children}</>;
        const href = String(link);
        const handleLinkClick = (e) => {
            if (hasMovedRef.current) {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        if (href.startsWith('http://') || href.startsWith('https://')) {
            return (
                <a href={href} onClick={handleLinkClick} draggable={false} style={{ display: 'block', width: '100%', height: '100%' }}>
                    {children}
                </a>
            );
        }
        return (
            <Link to={href} onClick={handleLinkClick} draggable={false} style={{ display: 'block', width: '100%', height: '100%' }}>
                {children}
            </Link>
        );
    };

    return (
        <div 
            style={{ width: '100%', position: 'relative', overflow: 'hidden', display: 'block', background: '#0f172a' }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            <style>{`
                /* Designed for 1920×900 (2.133:1) banner images */
                .pr-slider-frame { 
                    position: relative; 
                    width: 100%; 
                    height: clamp(320px, calc(100vw * 480 / 1920), 557px); 
                    overflow: hidden; 
                    user-select: none;
                    cursor: grab;
                    touch-action: pan-y;
                }
                .pr-slider-frame:active {
                    cursor: grabbing;
                }
                
                .pr-slide-item {
                    position: absolute;
                    inset: 0;
                    width: 100%;
                    height: 100%;
                    opacity: 0;
                    visibility: hidden;
                    transform: scale(1.035);
                    transition: opacity 0.75s cubic-bezier(0.25, 1, 0.35, 1), transform 1.1s cubic-bezier(0.25, 1, 0.35, 1), visibility 0.75s;
                    z-index: 1;
                    pointer-events: none;
                }
                
                .pr-slide-item.active {
                    opacity: 1;
                    visibility: visible;
                    transform: scale(1);
                    z-index: 2;
                    pointer-events: auto;
                }

                .pr-slider-image { 
                    width: 100%; 
                    height: 100%; 
                    object-fit: cover; 
                    object-position: center center; 
                    display: block; 
                }

                .pr-slider-arrow {
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 44px;
                    height: 44px;
                    border-radius: 22px;
                    background: rgba(9, 72, 89, 0.32);
                    backdrop-filter: blur(12px);
                    -webkit-backdrop-filter: blur(12px);
                    color: rgba(255, 255, 255, 0.85);
                    border: none !important;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    z-index: 20;
                    font-size: 22px;
                    transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
                    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.16);
                }

                .pr-slider-arrow svg {
                    opacity: 0.85;
                    stroke: #ffffff;
                    transition: opacity 0.2s ease, transform 0.2s ease;
                }

                .pr-slider-arrow:hover { 
                    background: #006282 !important; 
                    color: #ffffff !important; 
                    border: none !important;
                    transform: translateY(-50%) scale(1.08);
                    box-shadow: 0 6px 20px rgba(0, 98, 130, 0.45);
                }

                .pr-slider-arrow:hover svg {
                    opacity: 1;
                    stroke: #ffffff;
                }

                .pr-slider-arrow:active { 
                    background: #094859 !important; 
                    transform: translateY(-50%) scale(0.95);
                }

                .pr-slider-dots {
                    position: absolute;
                    bottom: 18px;
                    left: 50%;
                    transform: translateX(-50%);
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    z-index: 20;
                    padding: 5px 12px;
                    border-radius: 20px;
                    background: rgba(0, 0, 0, 0.28);
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                }

                .pr-slider-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 4px;
                    background: rgba(255, 255, 255, 0.55);
                    cursor: pointer;
                    transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
                }

                .pr-slider-dot:hover {
                    background: rgba(255, 255, 255, 0.9);
                }

                .pr-slider-dot.active {
                    width: 24px;
                    background: #007da5;
                    box-shadow: 0 0 10px rgba(0, 125, 165, 0.7);
                }

                @media (max-width: 768px) {
                    /* Mobile: scale height from the 1920×900 ratio */
                    .pr-slider-frame { height: clamp(240px, calc(100vw * 900 / 1920), 420px); }
                    .pr-slider-arrow { width: 36px !important; height: 36px !important; border-radius: 18px !important; font-size: 18px !important; }
                    .pr-slider-dots { bottom: 10px; padding: 4px 8px; gap: 6px; }
                    .pr-slider-dot { width: 6px; height: 6px; }
                    .pr-slider-dot.active { width: 18px; }
                }
            `}</style>

            <div 
                className="pr-slider-frame"
                onMouseDown={onPointerDown}
                onMouseMove={onPointerMove}
                onMouseUp={onPointerUp}
                onMouseLeave={onPointerUp}
                onTouchStart={onPointerDown}
                onTouchMove={onPointerMove}
                onTouchEnd={onPointerUp}
                onTouchCancel={onPointerUp}
            >
                {slides.map((slide, sIdx) => {
                    const isActive = sIdx === idx;
                    return (
                        <div 
                            key={sIdx} 
                            className={`pr-slide-item ${isActive ? 'active' : ''}`}
                            aria-hidden={!isActive}
                        >
                            <Wrap link={slide.link}>
                                <picture style={{ display: 'block', width: '100%', height: '100%' }}>
                                    {slide.imageMobile && (
                                        <source 
                                            media="(max-width: 768px)" 
                                            srcSet={ensureHttpsIfNeeded((function (u) { if (!u) return u; if (u.startsWith('http')) return u; const base = API_BASE_URL.replace(/\/api\/?$/i, ''); if (u.startsWith('/')) return base + u; return base + '/' + u; })(slide.imageMobile))} 
                                        />
                                    )}
                                    <img
                                        src={ensureHttpsIfNeeded((function (u) { if (!u) return u; if (u.startsWith('http')) return u; const base = API_BASE_URL.replace(/\/api\/?$/i, ''); if (u.startsWith('/')) return base + u; return base + '/' + u; })(slide.image))}
                                        alt={slide.title || `Promo ${sIdx + 1}`}
                                        className="pr-slider-image"
                                        onError={(e) => { try { e.currentTarget.onerror = null; e.currentTarget.src = 'https://picsum.photos/1800/460?random=1'; } catch (_) { } }}
                                        width="1920"
                                        height="557"
                                        loading={sIdx === 0 ? "eager" : "lazy"}
                                        fetchpriority={sIdx === 0 ? "high" : "auto"}
                                    />
                                </picture>
                            </Wrap>

                            {/* Overlay Content */}
                            {slide.title && (
                                <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                                    <div style={{ maxWidth: 1540, width: '100%', padding: '0 18px', boxSizing: 'border-box' }}>
                                        <div 
                                            className="text-lg sm:text-xl md:text-2xl font-extrabold text-white" 
                                            style={{ 
                                                textShadow: '0 6px 18px rgba(0,0,0,0.5)',
                                                transform: isActive ? 'translateY(0)' : 'translateY(14px)',
                                                opacity: isActive ? 1 : 0,
                                                transition: 'all 0.65s cubic-bezier(0.16, 1, 0.3, 1) 0.15s'
                                            }}
                                        >
                                            {slide.title}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}

                {count > 1 && (
                    <>
                        {/* left arrow */}
                        <button 
                            type="button"
                            aria-label="Previous banner" 
                            onClick={handlePrev}
                            className="pr-slider-arrow"
                            style={{ left: 14 }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                        </button>

                        {/* right arrow */}
                        <button 
                            type="button"
                            aria-label="Next banner" 
                            onClick={handleNext}
                            className="pr-slider-arrow"
                            style={{ right: 14 }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                        </button>

                        {/* Indicator Dots */}
                        <div className="pr-slider-dots">
                            {slides.map((_, dotIdx) => (
                                <div
                                    key={dotIdx}
                                    className={`pr-slider-dot ${dotIdx === idx ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        pauseAutoScrollTemporarily();
                                        setIdx(dotIdx);
                                    }}
                                    role="button"
                                    aria-label={`Slide ${dotIdx + 1}`}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function OfferSlidersModule({ config }) {
    const title = (config && config.title) || '';
    const items = useMemo(() => (config && Array.isArray(config.items)) ? config.items : [], [config]);
    const viewportRef = useRef(null);
    const [viewportWidth, setViewportWidth] = useState(0);
    const itemCount = items.length;

    // Minimum repeat multiplier to guarantee infinite buffer even on rapid clicking
    const repeatCount = useMemo(() => {
        if (!itemCount) return 0;
        return Math.max(5, Math.ceil(24 / itemCount));
    }, [itemCount]);

    const displayItems = useMemo(() => {
        if (!items || itemCount === 0) return [];
        const arr = [];
        for (let i = 0; i < repeatCount; i++) {
            arr.push(...items);
        }
        return arr;
    }, [items, itemCount, repeatCount]);

    const [startIndex, setStartIndex] = useState(() => (itemCount > 0 ? Math.floor(Math.max(5, Math.ceil(24 / itemCount)) / 2) * itemCount : 0));
    const [isTransitioning, setIsTransitioning] = useState(true);
    const [isPaused, setIsPaused] = useState(false);

    // Sync startIndex when itemCount or repeatCount changes
    useEffect(() => {
        if (itemCount > 0) {
            setStartIndex(Math.floor(repeatCount / 2) * itemCount);
        }
    }, [itemCount, repeatCount]);

    const resolveUrl = (u) => {
        if (!u) return u;
        if (u.startsWith('http://') || u.startsWith('https://')) return ensureHttpsIfNeeded(u);
        if (u.startsWith('//')) return window.location.protocol + u;
        const base = API_BASE_URL.replace(/\/api\/?$/i, '');
        return u.startsWith('/') ? base + u : base + '/' + u;
    };

    // Desktop must show exactly 3 items; smaller screens show fewer.
    const visibleCount = useMemo(() => {
        if (viewportWidth >= 1024) return 3;
        if (viewportWidth >= 640) return 2;
        return 1;
    }, [viewportWidth]);

    const gap = useMemo(() => {
        if (viewportWidth < 768) return 16;
        return 30; // Updated to 30px
    }, [viewportWidth]);

    const cardWidth = useMemo(() => {
        const w = viewportWidth || 0;
        if (!w) return 0;
        if (w < 768) return 276; // Match user requirement for mobile
        const gaps = gap * Math.max(0, visibleCount - 1);
        return Math.max(0, (w - gaps) / visibleCount);
    }, [viewportWidth, visibleCount, gap]);

    useEffect(() => {
        const el = viewportRef.current;
        if (!el) return;

        const update = () => setViewportWidth(el.clientWidth || 0);
        update();

        if (typeof ResizeObserver !== 'undefined') {
            const ro = new ResizeObserver(() => update());
            ro.observe(el);
            return () => ro.disconnect();
        }

        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    const pauseTimerRef = useRef(null);
    const pauseAutoScrollTemporarily = () => {
        setIsPaused(true);
        if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
        pauseTimerRef.current = setTimeout(() => {
            setIsPaused(false);
        }, 4000);
    };

    // Automatic smooth sliding every 3.5 seconds
    useEffect(() => {
        if (isPaused || itemCount <= 1) return;
        const interval = setInterval(() => {
            const middle = Math.floor(repeatCount / 2) * itemCount;
            const maxSafe = (repeatCount - 2) * itemCount;

            setStartIndex((curr) => {
                if (curr >= maxSafe) {
                    setIsTransitioning(false);
                    const resetIndex = middle + (curr % itemCount);
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            setIsTransitioning(true);
                            setStartIndex(resetIndex + 1);
                        });
                    });
                    return resetIndex;
                }
                setIsTransitioning(true);
                return curr + 1;
            });
        }, 3500);

        return () => clearInterval(interval);
    }, [isPaused, itemCount, repeatCount]);

    const handleTransitionEnd = () => {
        if (itemCount <= 0) return;
        const middle = Math.floor(repeatCount / 2) * itemCount;
        if (startIndex >= (repeatCount - 2) * itemCount) {
            setIsTransitioning(false);
            setStartIndex(middle + (startIndex % itemCount));
        } else if (startIndex <= itemCount) {
            setIsTransitioning(false);
            setStartIndex(middle + (startIndex % itemCount));
        }
    };

    const dragStartXRef = useRef(0);
    const isDraggingRef = useRef(false);
    const hasMovedRef = useRef(false);

    if (!items || items.length === 0) return null;

    const onPointerDown = (e) => {
        if (itemCount <= 1) return;
        const clientX = e.clientX ?? (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
        dragStartXRef.current = clientX;
        isDraggingRef.current = true;
        hasMovedRef.current = false;
    };

    const onPointerMove = (e) => {
        if (!isDraggingRef.current) return;
        const clientX = e.clientX ?? (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
        if (Math.abs(clientX - dragStartXRef.current) > 8) {
            hasMovedRef.current = true;
        }
    };

    const onPointerUp = (e) => {
        if (!isDraggingRef.current) return;
        isDraggingRef.current = false;
        const endX = e.clientX ?? (e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : dragStartXRef.current);
        const diffX = endX - dragStartXRef.current;
        if (Math.abs(diffX) >= 35) {
            if (diffX < 0) {
                handleNext();
            } else {
                handlePrev();
            }
        }
    };

    const Wrap = ({ link, children }) => {
        if (!link) return <>{children}</>;
        const handleLinkClick = (e) => {
            if (hasMovedRef.current) {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        if (String(link).startsWith('http://') || String(link).startsWith('https://')) {
            return <a href={link} onClick={handleLinkClick} draggable={false} style={{ textDecoration: 'none', color: 'inherit' }}>{children}</a>;
        }
        return <Link to={link} onClick={handleLinkClick} draggable={false} style={{ textDecoration: 'none', color: 'inherit' }}>{children}</Link>;
    };

    const handlePrev = () => {
        pauseAutoScrollTemporarily();
        const middle = Math.floor(repeatCount / 2) * itemCount;

        if (startIndex <= itemCount) {
            setIsTransitioning(false);
            const resetIndex = middle + (startIndex % itemCount);
            setStartIndex(resetIndex);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setIsTransitioning(true);
                    setStartIndex(resetIndex - 1);
                });
            });
        } else {
            setIsTransitioning(true);
            setStartIndex((prev) => prev - 1);
        }
    };

    const handleNext = () => {
        pauseAutoScrollTemporarily();
        const middle = Math.floor(repeatCount / 2) * itemCount;
        const maxSafe = (repeatCount - 2) * itemCount;

        if (startIndex >= maxSafe) {
            setIsTransitioning(false);
            const resetIndex = middle + (startIndex % itemCount);
            setStartIndex(resetIndex);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setIsTransitioning(true);
                    setStartIndex(resetIndex + 1);
                });
            });
        } else {
            setIsTransitioning(true);
            setStartIndex((prev) => prev + 1);
        }
    };

    return (
        <div style={{ width: '100%', marginTop: 48 }}>
            <div style={{ maxWidth: 1540, margin: '0 auto', padding: '0 18px', boxSizing: 'border-box' }}>
                {title ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h2 className="text-2xl sm:text-2xl md:text-3xl font-semibold" style={{ margin: 0, color: config?.titleColor || '#094859' }}>{title}</h2>
                    </div>
                ) : null}

                <style>{`
                    .pr-offers-wrap { position: relative; width: 100%; }
                    .pr-offers-viewport { overflow: hidden; padding: 6px 0; cursor: grab; touch-action: pan-y; user-select: none; }
                    .pr-offers-viewport:active { cursor: grabbing; }
                    .pr-offers-track { display: flex; gap: 30px; will-change: transform; align-items: stretch; }
                    .pr-offer-card { 
                        flex: 0 0 auto; 
                        will-change: transform, opacity;
                        transform-origin: center center;
                    }
                    .pr-offer-img { 
                        width: 100%; 
                        aspect-ratio: 16 / 9; 
                        border-radius: 12px; 
                        overflow: hidden; 
                        background: #f1f5f9; 
                        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
                        transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.35s cubic-bezier(0.16, 1, 0.3, 1);
                    }
                    .pr-offer-card:hover .pr-offer-img {
                        transform: translateY(-3px);
                        box-shadow: 0 10px 24px rgba(0, 125, 165, 0.16);
                    }
                    .pr-offer-img img { 
                        width: 100%; 
                        height: 100%; 
                        object-fit: cover; 
                        display: block; 
                        transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
                    }
                    .pr-offer-card:hover .pr-offer-img img {
                        transform: scale(1.04);
                    }
                    .pr-offer-title { 
                        margin-top: 10px; 
                        font-size: 18px; 
                        font-weight: 500; 
                        line-height: 1.35; 
                        color: #334155; 
                        text-align: start; 
                        transition: color 0.2s ease;
                    }
                    .pr-offer-card:hover .pr-offer-title {
                        color: #007da5;
                    }
                    .pr-offer-nav { display: flex; justify-content: flex-end; gap: 10px; margin-top: 14px; }
                    @media (max-width: 768px) {
                        .pr-offers-track { gap: 16px; }
                        .pr-offer-title { font-size: 16px; }
                        .pr-offer-card { width: 276px; }
                        .pr-offer-img { width: 100%; }
                    }
                `}</style>

                <div className="pr-offers-wrap">
                    <div
                        ref={viewportRef}
                        className="pr-offers-viewport"
                        onMouseEnter={() => setIsPaused(true)}
                        onMouseLeave={() => setIsPaused(false)}
                        onMouseDown={onPointerDown}
                        onMouseMove={onPointerMove}
                        onMouseUp={onPointerUp}
                        onTouchStart={onPointerDown}
                        onTouchMove={onPointerMove}
                        onTouchEnd={onPointerUp}
                        onTouchCancel={onPointerUp}
                    >
                        <div
                            className="pr-offers-track"
                            onTransitionEnd={handleTransitionEnd}
                            style={{
                                transform: `translateX(-${startIndex * (cardWidth + gap)}px)`,
                                transition: isTransitioning ? 'transform 420ms cubic-bezier(0.25, 1, 0.5, 1)' : 'none',
                            }}
                        >
                            {displayItems.map((it, i) => {
                                const isVisible = i >= startIndex && i < startIndex + visibleCount;
                                return (
                                    <div 
                                        key={i} 
                                        className={`pr-offer-card ${isVisible ? 'is-visible' : 'is-hidden'}`} 
                                        style={{ 
                                            width: cardWidth || undefined,
                                            opacity: isVisible ? 1 : 0.35,
                                            transform: isVisible ? 'scale(1) translateY(0)' : 'scale(0.96) translateY(2px)',
                                            transition: isTransitioning ? 'opacity 420ms cubic-bezier(0.25, 1, 0.5, 1), transform 420ms cubic-bezier(0.25, 1, 0.5, 1)' : 'none'
                                        }}
                                    >
                                        <Wrap link={it && it.link}>
                                            <div className="pr-offer-img">
                                                <img
                                                    src={resolveUrl(it && it.image)}
                                                    alt={(it && it.title) || `Offer ${i}`}
                                                    loading="lazy"
                                                    width="480"
                                                    height="270"
                                                    onError={(e) => { try { e.currentTarget.onerror = null; e.currentTarget.src = 'https://picsum.photos/seed/offer' + i + '/900/506'; } catch (_) { } }}
                                                />
                                            </div>
                                            {(it && it.title) ? <div className="pr-offer-title">{it.title}</div> : null}
                                        </Wrap>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {itemCount > 1 && (
                        <div className="pr-offer-nav">
                            <button
                                type="button"
                                className="glb-slider-nav-btn"
                                aria-label="Previous offers"
                                onClick={handlePrev}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                            </button>
                            <button
                                type="button"
                                className="glb-slider-nav-btn"
                                aria-label="Next offers"
                                onClick={handleNext}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


function HomeOfertaModule({ config }) {
    const title = config?.title || '';
    const leftSlides = config?.leftSlides || [];
    const rightSlides = config?.rightSlides || [];

    const Slider = ({ slides, ratio = "1/1", openNewTab = false, borderRadius = 16 }) => {
        const [idx, setIdx] = useState(0);
        const count = slides.length;
        const dragStartXRef = useRef(0);
        const isDraggingRef = useRef(false);
        const hasMovedRef = useRef(false);

        useEffect(() => {
            if (count <= 1) return;
            const interval = setInterval(() => setIdx(p => (p + 1) % count), 5000);
            return () => clearInterval(interval);
        }, [count]);

        if (count === 0) return <div style={{ width: '100%', height: '100%', background: '#eee', borderRadius: borderRadius }} />;

        const onPointerDown = (e) => {
            if (count <= 1) return;
            dragStartXRef.current = e.clientX ?? (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
            isDraggingRef.current = true;
            hasMovedRef.current = false;
        };

        const onPointerMove = (e) => {
            if (!isDraggingRef.current) return;
            const currentX = e.clientX ?? (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
            if (Math.abs(currentX - dragStartXRef.current) > 8) {
                hasMovedRef.current = true;
            }
        };

        const onPointerUp = (e) => {
            if (!isDraggingRef.current) return;
            isDraggingRef.current = false;
            const endX = e.clientX ?? (e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : dragStartXRef.current);
            const diffX = endX - dragStartXRef.current;
            if (Math.abs(diffX) >= 35) {
                if (diffX < 0) {
                    setIdx(i => (i + 1) % count);
                } else {
                    setIdx(i => (i - 1 + count) % count);
                }
            }
        };

        const handleLinkClick = (e) => {
            if (hasMovedRef.current) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        return (
            <div 
                style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', borderRadius: borderRadius, background: '#fff', cursor: count > 1 ? 'grab' : 'default', touchAction: 'pan-y', userSelect: 'none' }}
                onMouseDown={onPointerDown}
                onMouseMove={onPointerMove}
                onMouseUp={onPointerUp}
                onMouseLeave={onPointerUp}
                onTouchStart={onPointerDown}
                onTouchMove={onPointerMove}
                onTouchEnd={onPointerUp}
                onTouchCancel={onPointerUp}
            >
                {slides.map((s, sIdx) => {
                    const isActive = sIdx === idx;
                    const isExternal = s.link && (s.link.startsWith('http://') || s.link.startsWith('https://'));
                    const isNewTab = openNewTab || isExternal;
                    return (
                        <div
                            key={sIdx}
                            style={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                opacity: isActive ? 1 : 0,
                                visibility: isActive ? 'visible' : 'hidden',
                                transform: isActive ? 'scale(1)' : 'scale(1.03)',
                                transition: 'opacity 0.65s cubic-bezier(0.25, 1, 0.35, 1), transform 0.85s cubic-bezier(0.25, 1, 0.35, 1), visibility 0.65s',
                                pointerEvents: isActive ? 'auto' : 'none',
                                zIndex: isActive ? 2 : 1
                            }}
                        >
                            {isNewTab ? (
                                <a href={s.link || '#'} target="_blank" rel="noopener noreferrer" onClick={handleLinkClick} draggable={false} style={{ display: 'block', width: '100%', height: '100%' }}>
                                    <img
                                        src={resolveAssetUrl(s.image)}
                                        alt={s.title || ''}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        loading={sIdx === 0 ? "eager" : "lazy"}
                                        width="800"
                                        height="800"
                                        draggable={false}
                                        onError={(e) => { e.currentTarget.src = 'https://picsum.photos/seed/oferta/800/800'; }}
                                    />
                                </a>
                            ) : (
                                <Link to={s.link || '#'} onClick={handleLinkClick} draggable={false} style={{ display: 'block', width: '100%', height: '100%' }}>
                                    <img
                                        src={resolveAssetUrl(s.image)}
                                        alt={s.title || ''}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        loading={sIdx === 0 ? "eager" : "lazy"}
                                        width="800"
                                        height="800"
                                        draggable={false}
                                        onError={(e) => { e.currentTarget.src = 'https://picsum.photos/seed/oferta/800/800'; }}
                                    />
                                </Link>
                            )}
                        </div>
                    );
                })}
                {count > 1 && (
                    <>
                        <button type="button" className="ho-nav-btn glb-slider-nav-btn" aria-label="Slider para" onClick={() => setIdx(i => (i - 1 + count) % count)} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                        </button>
                        <button type="button" className="ho-nav-btn glb-slider-nav-btn" aria-label="Slider tjetër" onClick={() => setIdx(i => (i + 1) % count)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                        </button>
                    </>
                )}
            </div>
        );
    };

    return (
        <div style={{ width: '100%', marginTop: 48 }}>
            <div style={{ maxWidth: 1540, margin: '0 auto', padding: '0 18px', boxSizing: 'border-box' }}>
                {title && (
                    <h2 className="text-2xl sm:text-2xl md:text-3xl font-semibold" style={{ margin: '0 0 20px 0', color: config?.titleColor || '#094859' }}>{title}</h2>
                )}

                <style>{`
                    .ho-container { 
                        display: flex; 
                        width: 100%;
                        gap: 60px; 
                        align-items: stretch; 
                        justify-content: space-between; 
                        margin-bottom: 28px; 
                        box-sizing: border-box;
                    }
                    .ho-left { 
                        width: 450px;
                        flex: 0 0 450px;
                        height: 450px; 
                        min-height: 450px; 
                        aspect-ratio: 1 / 1;
                        overflow: hidden;
                        border-radius: 36px 0 36px 0 !important;
                    }
                    .ho-left > div { 
                        border-radius: 36px 0 36px 0 !important; 
                        overflow: hidden;
                    }
                    .ho-right { 
                        flex: 1 1 auto;
                        min-width: 0;
                        height: 450px; 
                        min-height: 450px; 
                        overflow: hidden;
                        border-radius: 32px !important;
                    }
                    .ho-right > div {
                        border-radius: 32px !important;
                        overflow: hidden;
                    }
                    .ho-left img, .ho-right img {
                        transition: transform 0.45s cubic-bezier(0.16, 1, 0.3, 1);
                        will-change: transform;
                    }
                    .ho-left:hover img, .ho-right:hover img {
                        transform: scale(1.04);
                    }
                    @media (max-width: 1400px) {
                        .ho-container { gap: 40px; }
                        .ho-left { width: 420px; flex: 0 0 420px; height: 420px; min-height: 420px; }
                        .ho-right { height: 420px; min-height: 420px; }
                    }
                    @media (max-width: 1200px) {
                        .ho-container { gap: 30px; }
                        .ho-left { width: 360px; flex: 0 0 360px; height: 360px; min-height: 360px; }
                        .ho-right { height: 360px; min-height: 360px; }
                    }
                    @media (max-width: 992px) {
                        .ho-container { gap: 20px; margin-bottom: 20px; }
                        .ho-left { width: 310px; flex: 0 0 310px; height: 310px; min-height: 310px; }
                        .ho-right { height: 310px; min-height: 310px; }
                    }
                    @media (max-width: 768px) {
                        .ho-container { 
                            display: flex !important; 
                            flex-direction: column !important; 
                            gap: 20px !important; 
                            margin-bottom: 24px !important; 
                        }
                        .ho-left { 
                            width: 100% !important; 
                            max-width: 100% !important;
                            flex: none !important; 
                            height: auto !important; 
                            min-height: 280px !important; 
                            aspect-ratio: 1 / 1 !important;
                            max-height: 420px !important;
                        }
                        .ho-right { 
                            width: 100% !important; 
                            flex: none !important; 
                            height: 200px !important; 
                            min-height: 200px !important; 
                            margin-top: 4px !important;
                        }
                    }
                    @media (max-width: 480px) {
                        .ho-left { 
                            height: 300px !important; 
                            min-height: 300px !important; 
                        }
                        .ho-right { 
                            height: 180px !important; 
                            min-height: 180px !important; 
                            margin-top: 4px !important;
                        }
                    }
                    .ho-nav-btn { 
                        width: 32px; 
                        height: 32px; 
                        border-radius: 8px; 
                        border: none !important; 
                        background: rgba(9, 72, 89, 0.32) !important; 
                        backdrop-filter: blur(10px);
                        -webkit-backdrop-filter: blur(10px);
                        color: rgba(255, 255, 255, 0.85) !important; 
                        display: inline-flex; 
                        align-items: center; 
                        justify-content: center; 
                        cursor: pointer; 
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);
                        transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1); 
                        user-select: none;
                    }
                    .ho-nav-btn svg {
                        width: 14px;
                        height: 14px;
                        stroke: #ffffff;
                        fill: none;
                        stroke-width: 2.5;
                        opacity: 0.85;
                        transition: opacity 0.2s ease;
                    }
                    .ho-nav-btn:hover { 
                        background: #006282 !important; 
                        border: none !important; 
                        color: #ffffff !important; 
                        transform: translateY(-50%) scale(1.08) !important; 
                        box-shadow: 0 4px 14px rgba(0, 98, 130, 0.4) !important;
                    }
                    .ho-nav-btn:hover svg {
                        opacity: 1;
                        stroke: #ffffff;
                    }
                    .ho-nav-btn:active { 
                        background: #094859 !important; 
                        border: none !important; 
                        color: #ffffff !important; 
                        transform: translateY(-50%) scale(0.95) !important; 
                    }
                `}</style>

                <div className="ho-container">
                    <div className="ho-left">
                        <Slider slides={leftSlides} openNewTab={true} borderRadius="36px 0 36px 0" />
                    </div>
                    <div className="ho-right">
                        <Slider slides={rightSlides} borderRadius="32px" />
                    </div>
                </div>
            </div>
        </div>
    );
}

function OfertaModule({ config }) {
    const title = config?.title || 'OFERTA SOT';
    const tabs = useMemo(() => config?.tabs || [], [config?.tabs]);
    const isOfertaSot = title === 'OFERTA SOT';
    const [activeTab, setActiveTab] = useState(isOfertaSot ? -1 : 0);
    const [sliderIdx, setSliderIdx] = useState(0);
    const viewportRef = useRef(null);
    const [viewportWidth, setViewportWidth] = useState(0);

    const allCards = useMemo(() => {
        return tabs.reduce((acc, t) => [...acc, ...(t.cards || [])], []);
    }, [tabs]);

    useEffect(() => {
        const el = viewportRef.current;
        if (!el) return;
        const update = () => setViewportWidth(el.clientWidth || 0);
        update();
        if (typeof ResizeObserver !== 'undefined') {
            const ro = new ResizeObserver(() => update());
            ro.observe(el);
            return () => ro.disconnect();
        }
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [activeTab]);

    if (tabs.length === 0) return null;

    const isAllMode = activeTab === -1 && isOfertaSot;
    const cards = isAllMode ? allCards : (tabs[activeTab]?.cards || []);

    const visibleCount = viewportWidth >= 1024 ? 3 : (viewportWidth >= 640 ? 2 : 1);
    const gap = 16;
    const cardWidth = viewportWidth ? (viewportWidth - (gap * (visibleCount - 1))) / visibleCount : 0;
    const maxIdx = Math.max(0, cards.length - visibleCount);

    return (
        <div style={{ width: '100%', marginTop: 32, marginBottom: 32 }}>
            <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 18px', boxSizing: 'border-box' }}>
                <style>{`
                    .oft-header {
                        display: flex;
                        align-items: stretch;
                        border: 1px solid #e5e7eb;
                        background: #fff;
                        margin-bottom: 24px;
                        // box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
                        overflow: hidden;
                        border-radius: 4px;
                    }
                    .oft-title-box {
                        background: #fff;
                        padding: 12px 32px;
                        display: flex;
                        align-items: center;
                        position: relative;
                        z-index: 2;
                        border-right: 1px solid #e5e7eb;
                        border-bottom: 3px solid transparent;
                        transition: all 0.2s;
                    }
                    .oft-title-box.can-click { cursor: pointer; }
                    .oft-title-box.active {
                        border-bottom-color: #F97316;
                    }
                    .oft-title-box::after {
                        content: "";
                        position: absolute;
                        right: -15px;
                        top: 0;
                        bottom: 0;
                        width: 30px;
                        background: #fff;
                        transform: skewX(-20deg);
                        border-right: 1px solid #e5e7eb;
                        z-index: 1;
                    }
                    .oft-title {
                        font-weight: 800;
                        font-size: 1.25rem;
                        color: ${config?.titleColor || '#003b49'};
                        text-transform: uppercase;
                        margin: 0;
                        position: relative;
                        z-index: 3;
                    }
                    .oft-tabs {
                        display: flex;
                        flex: 1;
                        background: #fff;
                        overflow-x: auto;
                        padding-left: 20px;
                    }
                    .oft-tab {
                        padding: 12px 24px;
                        font-size: 1rem;
                        font-weight: 600;
                        color: #4b5563;
                        cursor: pointer;
                        white-space: nowrap;
                        display: flex;
                        align-items: center;
                        transition: all 0.2s;
                        border-bottom: 3px solid transparent;
                    }
                    .oft-tab:hover { color: #003b49; }
                    .oft-tab.active {
                        color: #003b49;
                        border-bottom-color: #F97316;
                    }
                    .oft-slider-viewport {
                        overflow: hidden;
                        width: 100%;
                        position: relative;
                    }
                    .oft-slider-track {
                        display: flex;
                        gap: 16px;
                        transition: transform 0.3s ease-out;
                    }
                    .oft-card {
                        border: 2px solid #D6E4E8;
                        border-radius: 16px;
                        padding: 12px;
                        background: #F0F9FF;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        transition: transform 0.2s;
                        max-width: 320px;
                        margin: 0 auto;
                        width: 100%;
                        flex-shrink: 0;
                    }
                    .oft-card:hover { transform: translateY(-4px); }
                    .oft-card-img-wrap {
                        width: 100%;
                        aspect-ratio: 1 / 1;
                        // background: #fff;
                        border-radius: 12px;
                        overflow: hidden;
                        margin-bottom: 12px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                    .oft-card-img {
                        width: 90%;
                        height: 90%;
                        object-fit: contain;
                        border-radius: 12px;
                    }
                    .oft-btn {
                        width: 100%;
                        padding: 8px;
                        border-radius: 8px;
                        background: ${config?.buttonBgColor || '#fff'};
                        color: ${config?.buttonTextColor || '#003b49'};
                        font-weight: 700;
                        font-size: 0.9rem;
                        border: none;
                        cursor: pointer;
                        text-transform: uppercase;
                        transition: all 0.2s;
                    }
                    .oft-btn:hover {
                        background: ${config?.buttonHoverBgColor || '#F97316'};
                        color: ${config?.buttonHoverTextColor || '#fff'};
                    }
                    .oft-nav {
                        display: flex;
                        justify-content: center;
                        gap: 10px;
                        margin-top: 16px;
                    }
                    .oft-nav-btn {
                        width: 28px;
                        height: 28px;
                        border-radius: 6px;
                        background: #dedede;
                        color: #1a1a1a;
                        border: 1px solid #e6e6e6;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 16px;
                        transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
                    }
                    .oft-nav-btn:hover:not(:disabled) { 
                        background: #007da5 !important; 
                        border-color: #007da5 !important; 
                        color: #ffffff !important; 
                        transform: translateY(-1px);
                    }
                    .oft-nav-btn:active:not(:disabled) { 
                        background: #006080 !important; 
                        border-color: #006080 !important; 
                        transform: scale(0.94);
                    }
                    .oft-nav-btn:disabled {
                        opacity: 0.3;
                        cursor: default;
                    }
                    @media (max-width: 640px) {
                        .oft-title-box { padding: 10px 16px; }
                        .oft-title { font-size: 1rem; }
                    }
                `}</style>

                <div className="oft-header">
                    <div
                        className={`oft-title-box ${isOfertaSot ? 'can-click' : ''} ${activeTab === -1 ? 'active' : ''}`}
                        onClick={() => {
                            if (isOfertaSot) {
                                setActiveTab(-1);
                                setSliderIdx(0);
                            }
                        }}
                    >
                        <h2 className="oft-title">{title}</h2>
                    </div>
                    <div className="oft-tabs hide-scrollbar">
                        {tabs.map((tab, i) => (
                            <div
                                key={i}
                                className={`oft-tab ${activeTab === i ? 'active' : ''}`}
                                onClick={() => {
                                    setActiveTab(i);
                                    setSliderIdx(0);
                                }}
                            >
                                {tab.label}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="oft-slider-viewport" ref={viewportRef}>
                    <div className="oft-slider-track" style={{ transform: `translateX(-${sliderIdx * (cardWidth + gap)}px)` }}>
                        {cards.map((card, i) => (
                            <div key={i} className="oft-card" style={{ width: cardWidth }}>
                                <div className="oft-card-img-wrap">
                                    <img
                                        src={resolveAssetUrl(card.image)}
                                        alt="Offer"
                                        className="oft-card-img"
                                        loading="lazy"
                                        width="320"
                                        height="320"
                                        onError={(e) => { e.currentTarget.src = 'https://picsum.photos/seed/oft/400/400'; }}
                                    />
                                </div>
                                <Link to={card.link || '#'} style={{ width: '100%', textDecoration: 'none' }}>
                                    <button className="oft-btn">
                                        {card.buttonText || 'BLI TANI'}
                                    </button>
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>

                {cards.length > visibleCount && (
                    <div className="oft-nav">
                        <button
                            className="oft-nav-btn"
                            onClick={() => setSliderIdx(s => s <= 0 ? maxIdx : s - 1)}
                        >
                            ‹
                        </button>
                        <button
                            className="oft-nav-btn"
                            onClick={() => setSliderIdx(s => s >= maxIdx ? 0 : s + 1)}
                        >
                            ›
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export function HrSliderModule({ config }) {
    const slides = config?.slides || [];
    const [activeIdx, setActiveIdx] = useState(Math.floor(slides.length / 2));
    if (!slides.length) return null;

    return (
        <div style={{ width: '100%', padding: '60px 0', background: '#fff', overflow: 'hidden' }}>
            <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px', textAlign: 'center' }}>
                <div className="hrs-module-title">{config.title || 'PËRSE TË BËHESH PJESË E GLOBE?'}</div>

                <div className="hrs-slider-container">
                    <div className="hrs-slider-track" style={{ transform: `translateX(calc(-${activeIdx * (100 / (slides.length < 3 ? slides.length : 3))}% + ${slides.length < 3 ? 0 : 33.33}%))` }}>
                        {slides.map((s, i) => (
                            <div key={i} className={`hrs-slide ${i === activeIdx ? 'active' : ''}`}>
                                <div className="hrs-card">
                                    <Link to={s.link || '#'} className="hrs-img-link">
                                        <img src={resolveAssetUrl(s.image)} alt={s.title} loading="lazy" width="400" height="250" onError={(e) => { e.currentTarget.src = 'https://picsum.photos/seed/hr/600/400'; }} />
                                    </Link>
                                    <Link to={s.link || '#'} className="hrs-title-link">{s.title}</Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="hrs-dots">
                    {slides.map((_, i) => (
                        <div key={i} className={`hrs-dot ${i === activeIdx ? 'active' : ''}`} onClick={() => setActiveIdx(i)} aria-label={`Shko tek slide ${i + 1}`} role="button" />
                    ))}
                </div>
            </div>
            <style>{`
                .hrs-module-title {
                    font-size: 28px;
                    font-weight: 800;
                    color: #003b49;
                    margin-bottom: 40px;
                    border: 1px solid #e5e7eb;
                    display: inline-block;
                    padding: 14px 32px;
                    background: #fff;
                    text-transform: uppercase;
                }
                .hrs-slider-container {
                    position: relative;
                    width: 100%;
                    padding: 20px 0;
                }
                .hrs-slider-track {
                    display: flex;
                    transition: transform 0.5s ease;
                    align-items: center;
                }
                .hrs-slide {
                    flex: 0 0 33.33%;
                    padding: 0 15px;
                    transition: all 0.5s ease;
                    opacity: 0.5;
                    transform: scale(0.85);
                }
                .hrs-slide.active {
                    opacity: 1;
                    transform: scale(1.05);
                    z-index: 10;
                }
                .hrs-card {
                    background: #F0F9FF;
                    border-radius: 24px;
                    padding: 20px;
                    text-align: center;
                    position: relative;
                    border: 3px solid #D6E4E8;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }
                .hrs-card img {
                    width: 100%;
                    aspect-ratio: 16 / 10;
                    object-fit: cover;
                    border-radius: 12px;
                    margin-bottom: 20px;
                    background: #fff;
                }
                .hrs-img-link { width: 100%; display: block; overflow: hidden; border-radius: 12px; }
                .hrs-title-link {
                    display: block;
                    font-size: 14px;
                    font-weight: 800;
                    color: #003b49;
                    text-decoration: none;
                    text-transform: uppercase;
                    line-height: 1.3;
                    margin-top: 10px;
                }
                .hrs-dots {
                    display: flex;
                    justify-content: center;
                    gap: 12px;
                    margin-top: 40px;
                }
                .hrs-dot {
                    width: 12px;
                    height: 12px;
                    border-radius: 60%;
                    background: #d1d5db;
                    cursor: pointer;
                    transition: all 0.3s;
                }
                .hrs-dot.active {
                    background: #F97316;
                    width: 24px;
                    border-radius: 6px;
                }
                @media (max-width: 768px) {
                    .hrs-slide { flex: 0 0 100%; }
                    .hrs-slider-track { transform: none !important; display: block; }
                    .hrs-slide { opacity: 1; transform: none; padding: 10px 0; }
                    .hrs-dots { display: none; }
                }
            `}</style>
        </div>
    );
}

function SplitBannerModule({ left, right, config }) {
    return (
        <div style={{ width: '100%' }}>
            <div style={{ maxWidth: 1540, margin: '0 auto', padding: '0 18px', boxSizing: 'border-box' }}>
                <div className="hp-split-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 8, paddingBottom: 8 }}>
                    <p>TEST G</p>
                    <h1 className="!pl-[2%] text-xl sm:text-2xl md:text-3xl font-semibold pt-8" style={{ margin: 0, color: config?.titleColor || '#1a1a1a' }}>{config?.title || 'Katalog dhe Oferta'}</h1>
                </div>
                <style>{`
                    .hp-split-title { margin-top: 48px; margin-bottom: 20px; }
                    .hp-split-banner { display: flex; gap: 20px; border-radius: 12px; background: transparent; padding: 16px; align-items: stretch; justify-content: center; flex-wrap: nowrap; box-sizing: border-box; }
                    .hp-split-left { width: 45%; min-width: 0; display: flex; align-items: center; justify-content: center; height: 520px; border-radius: 20px; padding: 12px; overflow: hidden; }
                    .hp-split-right { width: 60%; min-width: 0; display: flex; align-items: center; justify-content: center; height: 520px; border-radius: 20px; padding: 12px; overflow: hidden; }
                    .hp-split-left a, .hp-split-right a { overflow: hidden; border-radius: 16px; }
                    .hp-split-left img, .hp-split-right img { transition: transform 0.45s cubic-bezier(0.16, 1, 0.3, 1); will-change: transform; }
                    .hp-split-left:hover img, .hp-split-right:hover img { transform: scale(1.04); }
                    @media (max-width: 768px) {
                        .hp-split-title { margin-top: 16px; margin-bottom: 12px; }
                        .hp-split-banner { flex-direction: column !important; height: auto !important; }
                        .hp-split-left, .hp-split-right { width: 100% !important; height: 300px !important; }
                    }
                `}</style>
                <div className="hp-split-banner">
                    {/* Left Card */}
                    <div className="hp-split-left">
                        <Link to={left.link || '#'} style={{ width: '100%', height: '100%', display: 'block' }}>
                            <img src={ensureHttpsIfNeeded((function (u) { if (!u) return 'https://picsum.photos/seed/left/800/420'; if (u.startsWith('http')) return u; const base = API_BASE_URL.replace(/\/api\/?$/i, ''); return u.startsWith('/') ? base + u : base + '/' + u; })(left.image))} alt={left.alt || "Left Banner"} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: 16 }}
                                loading="lazy" width="600" height="520"
                                onError={(e) => { try { e.currentTarget.onerror = null; e.currentTarget.src = 'https://picsum.photos/seed/left/800/420'; } catch (_) { } }} />
                        </Link>
                    </div>

                    {/* Right Card */}
                    <div className="hp-split-right">
                        <Link to={right.link || '#'} style={{ width: '100%', height: '100%', display: 'block' }}>
                            <img src={ensureHttpsIfNeeded((function (u) { if (!u) return 'https://picsum.photos/seed/right/800/420'; if (u.startsWith('http')) return u; const base = API_BASE_URL.replace(/\/api\/?$/i, ''); return u.startsWith('/') ? base + u : base + '/' + u; })(right.image))} alt={right.alt || "Right Banner"} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: 16 }}
                                loading="lazy" width="900" height="520"
                                onError={(e) => { try { e.currentTarget.onerror = null; e.currentTarget.src = 'https://picsum.photos/seed/right/800/420'; } catch (_) { } }} />
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}



function ImageModule({ url, alt, width, height }) {
    if (!url) return null;
    const w = Number(width || 0);
    const h = Number(height || 0);
    const safeW = Number.isFinite(w) && w > 0 ? w : 0;
    const safeH = Number.isFinite(h) && h > 0 ? h : 0;
    return (
        <div style={{ width: '100%', marginTop: 42, boxSizing: 'border-box' }}>
            <div style={{ maxWidth: 1540, margin: '0 auto', padding: '0 18px', boxSizing: 'border-box' }}>
                <div
                    style={{
                        borderRadius: 10,
                        overflow: 'hidden',
                        background: '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ...(safeW ? { width: Math.min(safeW, 1540), marginLeft: 'auto', marginRight: 'auto' } : {}),
                        ...(safeH ? { height: safeH } : {}),
                    }}
                >
                    <img
                        src={resolveAssetUrl(url)}
                        alt={alt || 'Image'}
                        style={{
                            width: safeH ? '100%' : '100%',
                            height: safeH ? '100%' : 'auto',
                            maxWidth: '100%',
                            display: 'block',
                            objectFit: safeH ? 'contain' : 'contain',
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

function SpacerModule({ config }) {
    const height = Number(config && config.height);
    const safeHeight = Number.isFinite(height) && height > 0 ? height : 0;
    if (!safeHeight) return null;
    return <div style={{ width: '100%', height: safeHeight }} />;
}

function FeaturePromoReverseModule({ config }) {
    const promoTitle = (config && config.promoTitle) || 'Më e mira për shtëpinë';
    const promoSubtitle = (config && config.promoSubtitle) || 'teknologjia AEG';
    const promoText = (config && config.promoText) || '';
    const promoImage = ensureHttpsIfNeeded((config && config.promoImage) || bannerImg2);
    const promoImageAlt = (config && config.promoImageAlt) || 'img_2';

    const promoButtonText = (config && config.promoButtonText) || '';
    const promoButtonLink = (config && config.promoButtonLink) || '';
    const uid = useMemo(() => Math.random().toString(36).substring(2, 9), []);

    return (
        <section style={{ width: '100%', marginTop: 82, boxSizing: 'border-box' }}>
            <div className="flex items-center justify-center mb-10">
                <div className="group flex flex-col md:flex-row w-[94%] sm:w-[90%] lg:w-[80%] lg:h-[450px] md:min-h-[400px] uppercase rounded-3xl border-2 border-solid border-gray-300 overflow-hidden">
                    <div className="w-full md:w-[60%] h-[250px] sm:h-[350px] md:h-auto bg-[#f9fafb] overflow-hidden">
                        <img className="w-full h-full object-cover block" src={promoImage} alt={promoImageAlt} style={{ transition: 'transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)', willChange: 'transform' }} />
                    </div>
                    <div className="w-full md:w-[40%] p-6 sm:p-10 overflow-hidden flex flex-col justify-center">
                        <div className="flex flex-col h-full">
                            <div>
                                <h1 className="text-xl sm:text-2xl md:text-2xl lg:text-3xl xl:text-3xl py-4" style={{ color: config?.titleColor || 'inherit', fontWeight: config?.titleBold ? 'bold' : 'normal', letterSpacing: '-0.6px' }}>{promoTitle}</h1>
                                <h2 className="text-xl sm:text-2xl md:text-2xl lg:text-3xl xl:text-3xl" style={{ color: config?.subtitleColor || 'inherit', fontWeight: config?.subtitleBold ? 'bold' : 'normal', letterSpacing: '-0.5px' }}>{promoSubtitle}</h2>
                            </div>
                            <div className="flex-1 mt-4">
                                {promoText ? (
                                    <p className="normal-case text-sm md:text-base lg:text-base xl:text-lg text-black-600 !pr-8 leading-loose" style={{ color: config?.textColor || 'inherit', fontWeight: config?.textBold ? 'bold' : 450, letterSpacing: '-0.2px' }}>{promoText}</p>
                                ) : null}
                            </div>
                            {promoButtonText && (
                                <div className="mt-6">
                                    <style>{`
                                        .fpr-btn-${uid} { background: #007da5; color: #ffffff; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); letter-spacing: -0.25px; }
                                        .fpr-btn-${uid}:hover { background: #f48d21; color: #ffffff; transform: translateY(-3px) scale(1.03); box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05); }
                                    `}</style>
                                    <Link to={promoButtonLink || '#'} className={`fpr-btn-${uid} inline-block px-8 py-3 rounded-xl font-bold text-sm transition-colors`} style={{ letterSpacing: '-0.25px' }}>
                                        {promoButtonText}
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function TextModule({ text }) {
    if (!text) return null;
    return (
        <div style={{ maxWidth: 1540, margin: '24px auto', padding: '0 18px' }}>
            <div className="ql-editor" dangerouslySetInnerHTML={{ __html: text }} />
        </div>
    )
}

function normalizeHtmlContent(html) {
    const raw = String(html || '');
    if (raw.includes('<')) return raw;
    if (!raw.includes('&lt;') && !raw.includes('&gt;') && !raw.includes('&amp;')) return raw;
    return raw
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, '&');
}

function HtmlModule({ html }) {
    const containerRef = useRef(null);
    const normalized = useMemo(() => normalizeHtmlContent(html), [html]);

    // If pure JS without HTML tags, wrap in <script> so it runs automatically
    const contentToRender = useMemo(() => {
        if (!normalized || !normalized.trim()) return '';
        const trimmed = normalized.trim();
        const hasHtmlTags = /<\/?[a-z!][\s\S]*>/i.test(trimmed);
        if (!hasHtmlTags) {
            let isJsCode = false;
            try {
                // eslint-disable-next-line no-new-func
                new Function(trimmed);
                isJsCode = true;
            } catch (_) {
                isJsCode = /\b(var|let|const|function|window|document|console|alert|addEventListener|setInterval|setTimeout)\b/.test(trimmed);
            }
            if (isJsCode) {
                return `<script>${trimmed}</script>`;
            }
        }
        return normalized;
    }, [normalized]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !contentToRender) return;

        let isCancelled = false;
        const oldScripts = Array.from(container.querySelectorAll('script'));
        if (!oldScripts.length) return;

        const executeScriptsSequentially = async () => {
            for (const oldScript of oldScripts) {
                if (isCancelled || !container) return;

                await new Promise((resolve) => {
                    try {
                        const newScript = document.createElement('script');

                        // Copy all attributes (src, async, defer, id, class, type, etc.)
                        for (let i = 0; i < oldScript.attributes.length; i++) {
                            const attr = oldScript.attributes[i];
                            newScript.setAttribute(attr.name, attr.value);
                        }

                        if (!newScript.type) {
                            newScript.type = 'text/javascript';
                        }

                        if (oldScript.src) {
                            // External script: wait for it to load before proceeding
                            newScript.onload = () => resolve();
                            newScript.onerror = (e) => {
                                console.warn('HtmlModule: Failed to load external script:', oldScript.src, e);
                                resolve();
                            };
                            if (oldScript.parentNode) {
                                oldScript.parentNode.replaceChild(newScript, oldScript);
                            } else {
                                container.appendChild(newScript);
                            }
                        } else {
                            // Inline script: set code and insert into DOM to execute
                            const code = oldScript.text || oldScript.textContent || oldScript.innerHTML || '';
                            newScript.text = code;
                            if (oldScript.parentNode) {
                                oldScript.parentNode.replaceChild(newScript, oldScript);
                            } else {
                                container.appendChild(newScript);
                            }
                            resolve();
                        }
                    } catch (err) {
                        console.error('HtmlModule: Script execution error:', err);
                        resolve();
                    }
                });
            }

            // Dispatch DOMContentLoaded & load events for any listeners waiting for page ready
            if (!isCancelled && (document.readyState === 'complete' || document.readyState === 'interactive')) {
                try {
                    document.dispatchEvent(new Event('DOMContentLoaded'));
                    window.dispatchEvent(new Event('load'));
                } catch (_) { }
            }
        };

        executeScriptsSequentially();

        return () => {
            isCancelled = true;
        };
    }, [contentToRender]);

    if (!html) return null;

    return (
        <div style={{ maxWidth: 1540, margin: '24px auto', padding: '0 18px', width: '100%' }}>
            <div
                ref={containerRef}
                className="html-module-content"
                style={{ width: '100%' }}
                dangerouslySetInnerHTML={{ __html: contentToRender }}
            />
        </div>
    );
}

/* ─────────────────────────────────────────
   OLD-TO-NEW OFFER MODULE
   Full React component — no dangerouslySetInnerHTML,
   no addEventListener, no !important fighting.
───────────────────────────────────────── */

const GLB_OTN_DEFAULT_ICON = (
    <svg viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="1.7" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

const GLB_OTN_ICON_MAP = {
    '3031': <svg viewBox="0 0 24 24"><rect x="5" y="2.5" width="14" height="19" rx="1.5"/><path d="M7.5 6h9"/><circle cx="12" cy="14" r="4.5"/><circle cx="8" cy="4.5" r=".6"/></svg>,
    '3032': <svg viewBox="0 0 24 24"><path d="M9 3h6"/><path d="M10 3v5l-3 4v6a3 3 0 0 0 3 3h4a3 3 0 0 0 3-3v-6l-3-4V3"/><path d="M9 12h6"/></svg>,
    '3033': <svg viewBox="0 0 24 24"><rect x="7" y="2.5" width="10" height="19" rx="1.7"/><path d="M10 5h4"/><path d="M11 18.5h2"/></svg>,
    '3034': <svg viewBox="0 0 24 24"><rect x="2.5" y="5" width="19" height="12" rx="1.5"/><path d="M9 20h6"/><path d="M12 17v3"/></svg>,
    '3035': <svg viewBox="0 0 24 24"><rect x="2.5" y="6" width="19" height="8" rx="1.5"/><path d="M6 10h12"/><path d="M8 17v2"/><path d="M16 17v2"/></svg>,
    '3036': <svg viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="11" rx="1.5"/><path d="M2.5 19h19"/><path d="M8 15l-1 4"/><path d="M16 15l1 4"/></svg>,
    '3037': <svg viewBox="0 0 24 24"><path d="M5 5h8a4 4 0 0 1 0 8H8"/><path d="M8 13v8"/><path d="M13 8h6"/><path d="M17 6v4"/></svg>,
    '3038': <svg viewBox="0 0 24 24"><path d="M4 17h16l-3-8H9z"/><path d="M9 9c1-3 4-4 7-3"/><path d="M4 17v2h16"/></svg>,
    '2812': <svg viewBox="0 0 24 24"><path d="M8 6l1.5-2h5L16 6h3.5A1.5 1.5 0 0 1 21 7.5v10A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5v-10A1.5 1.5 0 0 1 4.5 6z"/><circle cx="12" cy="12.5" r="3.5"/></svg>,
    '2813': <svg viewBox="0 0 24 24"><path d="M7 8h10a4 4 0 0 1 3.5 5.9l-1.3 2.5a2.5 2.5 0 0 1-4 .6l-1.3-1.3h-3.8L8.8 17a2.5 2.5 0 0 1-4-.6l-1.3-2.5A4 4 0 0 1 7 8z"/><path d="M7 12h4"/><path d="M9 10v4"/><circle cx="16.5" cy="11.5" r=".7"/><circle cx="18.5" cy="13.5" r=".7"/></svg>,
    '2814': <svg viewBox="0 0 24 24"><path d="M4 6h12l4 4-4 4H4z"/><circle cx="10" cy="10" r="2.5"/><path d="M7 14v4"/><path d="M4 18h6"/></svg>,
    '2815': <svg viewBox="0 0 24 24"><rect x="7" y="4" width="10" height="17" rx="1.5"/><path d="M10 2h4v2"/><path d="M13 7l-3 6h3l-2 5 5-7h-3z"/></svg>,
};

const GLB_OTN_CATEGORIES_PRESET = [
    { id: '3031', search: 'elektroshtepiake te medha lavatrice frigorifer', label: 'Elektroshtepiake te medha', topParentId: '3031', icon: GLB_OTN_ICON_MAP['3031'] },
    { id: '3032', search: 'elektroshtepiake te vogla', label: 'Elektroshtepiake te vogla', topParentId: '3032', icon: GLB_OTN_ICON_MAP['3032'] },
    { id: '3033', search: 'telefonia celulare smartphone telefon', label: 'Telefonia', topParentId: '3033', icon: GLB_OTN_ICON_MAP['3033'] },
    { id: '3034', search: 'tv dhe audio televizore', label: 'TV & Audio', topParentId: '3034', icon: GLB_OTN_ICON_MAP['3034'] },
    { id: '3035', search: 'ftohje dhe ngrohje kondicioner', label: 'Ftohje & Ngrohje', topParentId: '3035', icon: GLB_OTN_ICON_MAP['3035'] },
    { id: '3036', search: 'kompjutera laptop', label: 'Kompjutera', topParentId: '3036', icon: GLB_OTN_ICON_MAP['3036'] },
    { id: '3037', search: 'kujdesi personal', label: 'Kujdesi Personal', topParentId: '3037', icon: GLB_OTN_ICON_MAP['3037'] },
    { id: '3038', search: 'pastrimi dhe hekurosja fshise hekur', label: 'Pastrimi & Hekurosja', topParentId: '3038', icon: GLB_OTN_ICON_MAP['3038'] },
    { id: '2812', search: 'foto dhe video kamera', label: 'Foto & Video', topParentId: '2812', icon: GLB_OTN_ICON_MAP['2812'] },
    { id: '2813', search: 'lojra dhe hobi ps5 xbox', label: 'Lojra & Hobi', topParentId: '2813', icon: GLB_OTN_ICON_MAP['2813'] },
    { id: '2814', search: 'siguria dhe mbikqyrja', label: 'Siguria & Mbikqyrja', topParentId: '2814', icon: GLB_OTN_ICON_MAP['2814'] },
    { id: '2815', search: 'bateri dhe llampa', label: 'Bateri & Llampa', topParentId: '2815', icon: GLB_OTN_ICON_MAP['2815'] },
];

function flattenCategoryTree(tree, parentCategory = null) {
    let list = [];
    if (!Array.isArray(tree)) return list;

    for (const cat of tree) {
        if (!cat || !cat.id) continue;
        const topParent = parentCategory ? parentCategory.topParent : cat;
        const topParentIdStr = String(topParent ? topParent.id : cat.id);
        const catIdStr = String(cat.id);

        const searchTerms = [
            cat.name,
            parentCategory ? parentCategory.name : '',
            topParent ? topParent.name : ''
        ].filter(Boolean).join(' ').toLowerCase();

        list.push({
            id: catIdStr,
            label: cat.name,
            search: searchTerms,
            topParentId: topParentIdStr,
            icon: GLB_OTN_ICON_MAP[topParentIdStr] || GLB_OTN_ICON_MAP[catIdStr] || GLB_OTN_DEFAULT_ICON
        });

        if (Array.isArray(cat.subcategories) && cat.subcategories.length > 0) {
            list = list.concat(flattenCategoryTree(cat.subcategories, {
                id: cat.id,
                name: cat.name,
                topParent: topParent
            }));
        }
    }

    return list;
}

/* CSS injected once into <head> — selectors use .glb-category-details / .glb-category-summary
   (not details/summary element selectors) so they match our div/button JSX */
const GLB_OTN_CSS = `
    #glb-online-box,:root{--glb-blue:#0085a8;--glb-blue-dark:#006f8d;--glb-dark:#003e4e;--glb-orange:#f7931e;--glb-border:#61bfdd;--glb-text:#242424;--glb-placeholder:#929292;--glb-error:#c62828;--glb-dropdown-bg:#f3f3f3;--glb-dropdown-hover:#e7f4f7}
    #glb-online-box,#glb-online-box *,#glb-online-box *::before,#glb-online-box *::after{box-sizing:border-box!important}
    #glb-online-box,#glb-online-box *{white-space:normal!important}
    #glb-online-box{display:block!important;width:100%!important;height:auto!important;min-height:0!important;margin:0!important;padding:0!important;color:var(--glb-text)!important;font-family:'Inter',system-ui,-apple-system,sans-serif!important;line-height:1.2!important;text-align:left!important;background:#ffffff!important}
    #glb-online-box .glb-layout{display:grid!important;grid-template-columns:minmax(0,1fr)!important;grid-auto-rows:max-content!important;align-content:start!important;align-items:start!important;justify-items:start!important;width:100%!important;max-width:1120px!important;margin:0!important;padding:30px 30px 35px 70px!important;gap:28px 0!important}
    #glb-online-box .glb-title{display:block!important;width:100%!important;margin:0!important;padding:0!important;color:var(--glb-dark)!important;font-family:'Inter',system-ui,-apple-system,sans-serif!important;font-size:54px!important;font-weight:700!important;line-height:1!important;letter-spacing:-1px!important;text-align:left!important;white-space:nowrap!important}
    #glb-online-box .glb-title-part{display:inline!important;margin:0!important;padding:0!important;white-space:nowrap!important}
    #glb-online-box .glb-steps-wrapper{display:block!important;width:100%!important;margin:0!important;padding:0!important;overflow-x:auto!important;overflow-y:hidden!important;scrollbar-width:none!important}
    #glb-online-box .glb-steps-wrapper::-webkit-scrollbar{display:none!important}
    #glb-online-box .glb-steps{display:flex!important;flex-direction:row!important;align-items:center!important;justify-content:flex-start!important;flex-wrap:nowrap!important;width:max-content!important;min-width:100%!important;margin:0!important;padding:0!important;gap:24px!important}
    #glb-online-box .glb-step,#glb-online-box .glb-step.active,#glb-online-box .glb-step.completed,#glb-online-box .glb-step:not(.active):not(.completed){display:flex!important;flex:0 0 auto!important;flex-direction:row!important;align-items:center!important;justify-content:flex-start!important;width:auto!important;height:auto!important;margin:0!important;padding:0!important;gap:10px!important;color:var(--glb-text)!important;font-family:'Inter',system-ui,-apple-system,sans-serif!important;font-size:18px!important;font-weight:700!important;line-height:1.15!important;text-align:left!important;white-space:nowrap!important;opacity:1!important;filter:none!important}
    #glb-online-box .glb-step-text{margin:0!important;padding:0!important;color:var(--glb-text)!important;font-weight:700!important;opacity:1!important;filter:none!important;white-space:nowrap!important}
    #glb-online-box .glb-step-number,#glb-online-box .glb-step .glb-step-number,#glb-online-box .glb-step.active .glb-step-number,#glb-online-box .glb-step.completed .glb-step-number,#glb-online-box .glb-step:not(.active):not(.completed) .glb-step-number{display:flex!important;flex:0 0 38px!important;align-items:center!important;justify-content:center!important;width:38px!important;height:38px!important;min-width:38px!important;min-height:38px!important;margin:0!important;padding:0!important;color:#ffffff!important;font-size:17px!important;font-weight:700!important;line-height:1!important;background:var(--glb-orange)!important;border-radius:50%!important;opacity:1!important;filter:none!important}
    #glb-online-box .glb-form{display:grid!important;grid-template-columns:minmax(0,1fr)!important;grid-auto-rows:max-content!important;align-content:start!important;align-items:start!important;justify-items:stretch!important;width:642px!important;max-width:100%!important;height:auto!important;min-height:0!important;margin:0!important;padding:0!important;gap:13px 0!important}
    #glb-online-box .glb-input{display:block!important;width:100%!important;height:49px!important;min-height:49px!important;margin:0!important;padding:0 25px!important;color:var(--glb-text)!important;font-family:'Inter',system-ui,-apple-system,sans-serif!important;font-size:16px!important;font-weight:400!important;line-height:normal!important;text-align:left!important;background:#ffffff!important;border:2px solid var(--glb-border)!important;border-radius:27px!important;outline:none!important;box-shadow:none!important}
    #glb-online-box .glb-input::placeholder{color:var(--glb-placeholder)!important;opacity:1!important}
    #glb-online-box .glb-input:focus{border-color:var(--glb-blue)!important;box-shadow:0 0 0 3px rgba(0,133,168,0.12)!important}
    #glb-online-box .glb-input.invalid{border-color:var(--glb-error)!important}
    #glb-online-box .glb-error{display:none!important;width:100%!important;height:auto!important;margin:-5px 0 0!important;padding:0!important;color:var(--glb-error)!important;font-family:'Inter',system-ui,-apple-system,sans-serif!important;font-size:12px!important;font-weight:600!important;line-height:1.2!important;text-align:left!important}
    #glb-online-box .glb-error.visible{display:block!important}
    #glb-online-box .glb-category-details{display:block!important;position:relative!important;justify-self:start!important;width:390px!important;max-width:100%!important;height:auto!important;min-height:0!important;margin:0!important;padding:0!important;font-size:0!important;line-height:0!important;white-space:normal!important;background:transparent!important;border:0!important;box-shadow:none!important}
    #glb-online-box .glb-category-details>.glb-category-summary{display:flex!important;align-items:center!important;justify-content:center!important;width:100%!important;height:56px!important;min-height:56px!important;max-height:56px!important;margin:0!important;padding:0 20px!important;gap:10px!important;color:#ffffff!important;font-family:'Inter',system-ui,-apple-system,sans-serif!important;font-size:16px!important;font-weight:700!important;line-height:1!important;text-align:center!important;white-space:nowrap!important;background:var(--glb-blue)!important;border:0!important;border-radius:30px!important;outline:none!important;box-shadow:none!important;cursor:pointer!important;appearance:none!important;-webkit-appearance:none!important}
    #glb-online-box .glb-category-details>.glb-category-summary,#glb-online-box .glb-category-details>.glb-category-summary *{cursor:pointer!important}
    #glb-online-box .glb-category-details>.glb-category-summary:hover{background:var(--glb-blue-dark)!important}
    #glb-online-box .glb-category-details>.glb-category-summary:focus-visible{box-shadow:0 0 0 4px rgba(0,133,168,0.22)!important}
    #glb-online-box .glb-button-arrow{display:inline-block!important;flex:0 0 auto!important;width:9px!important;height:9px!important;margin:-4px 0 0!important;padding:0!important;border-right:2px solid #ffffff!important;border-bottom:2px solid #ffffff!important;transform:rotate(45deg)!important;transition:transform 0.2s ease,margin 0.2s ease!important}
    #glb-online-box .glb-category-details.is-open .glb-button-arrow{margin-top:4px!important;transform:rotate(225deg)!important}
    #glb-online-box .glb-category-details>.glb-category-dropdown{display:block!important;position:relative!important;width:100%!important;height:auto!important;min-height:0!important;max-height:300px!important;margin:10px 0 0!important;padding:5px 0!important;font-size:0!important;line-height:0!important;white-space:normal!important;background:var(--glb-dropdown-bg)!important;border:0!important;border-radius:10px!important;box-shadow:none!important;overflow-y:auto!important}
    #glb-online-box .glb-category-dropdown::before,#glb-online-box .glb-category-dropdown::after,#glb-online-box .glb-category-option::before,#glb-online-box .glb-category-option::after{display:none!important;content:none!important}
    #glb-online-box .glb-category-option{display:block!important;position:relative!important;width:100%!important;height:40px!important;min-height:40px!important;max-height:40px!important;margin:0!important;padding:0!important;font-size:0!important;line-height:0!important;background:transparent!important;border:0!important}
    #glb-online-box a.glb-category-item{display:flex!important;flex-direction:row!important;align-items:center!important;justify-content:flex-start!important;width:100%!important;height:40px!important;min-height:40px!important;max-height:40px!important;margin:0!important;padding:0 14px!important;gap:11px!important;color:#111111!important;font-family:'Inter',system-ui,-apple-system,sans-serif!important;font-size:13px!important;font-weight:700!important;line-height:1.15!important;text-align:left!important;text-decoration:none!important;text-transform:uppercase!important;white-space:nowrap!important;background:transparent!important;border:0!important;border-radius:0!important;box-shadow:none!important;cursor:pointer!important;pointer-events:auto!important}
    #glb-online-box a.glb-category-item,#glb-online-box a.glb-category-item *,#glb-online-box .glb-category-icon,#glb-online-box .glb-category-name{cursor:pointer!important}
    #glb-online-box a.glb-category-item:hover{color:var(--glb-blue)!important;text-decoration:none!important;background:var(--glb-dropdown-hover)!important}
    #glb-online-box a.glb-category-item:focus-visible{color:var(--glb-blue)!important;background:var(--glb-dropdown-hover)!important;outline:2px solid var(--glb-blue)!important;outline-offset:-2px!important}
    #glb-online-box .glb-category-icon{display:flex!important;flex:0 0 24px!important;align-items:center!important;justify-content:center!important;width:24px!important;height:24px!important;min-width:24px!important;min-height:24px!important;max-width:24px!important;max-height:24px!important;margin:0!important;padding:0!important;color:#39b9d8!important;line-height:1!important;pointer-events:none!important}
    #glb-online-box .glb-category-icon svg{display:block!important;width:22px!important;height:22px!important;min-width:22px!important;min-height:22px!important;max-width:22px!important;max-height:22px!important;margin:0!important;padding:0!important;fill:none!important;stroke:currentColor!important;stroke-width:1.7!important;stroke-linecap:round!important;stroke-linejoin:round!important;pointer-events:none!important}
    #glb-online-box .glb-category-name{display:block!important;flex:1 1 auto!important;align-self:center!important;width:auto!important;height:auto!important;margin:0!important;padding:0!important;color:inherit!important;font-family:inherit!important;font-size:13px!important;font-weight:700!important;line-height:1.15!important;text-align:left!important;white-space:nowrap!important;pointer-events:none!important}
    @media(max-width:1000px){#glb-online-box .glb-layout{padding:25px 25px 30px 35px!important;gap:24px 0!important}#glb-online-box .glb-title{font-size:44px!important}#glb-online-box .glb-steps{gap:18px!important}#glb-online-box .glb-step,#glb-online-box .glb-step.active,#glb-online-box .glb-step.completed,#glb-online-box .glb-step:not(.active):not(.completed){gap:8px!important;font-size:15px!important;opacity:1!important}#glb-online-box .glb-step-number{flex:0 0 34px!important;width:34px!important;height:34px!important;min-width:34px!important;min-height:34px!important;font-size:15px!important}}
    @media(max-width:650px){#glb-online-box .glb-layout{width:100%!important;padding:28px 16px 25px!important;gap:28px 0!important}#glb-online-box .glb-title{width:100%!important;font-size:34px!important;line-height:0.98!important;letter-spacing:-0.6px!important;white-space:normal!important}#glb-online-box .glb-title-part{display:block!important;width:100%!important;white-space:normal!important}#glb-online-box .glb-steps-wrapper{overflow:visible!important}#glb-online-box .glb-steps{display:grid!important;grid-template-columns:repeat(3,minmax(0,1fr))!important;align-items:start!important;width:100%!important;min-width:0!important;gap:7px!important}#glb-online-box .glb-step,#glb-online-box .glb-step.active,#glb-online-box .glb-step.completed,#glb-online-box .glb-step:not(.active):not(.completed){display:flex!important;flex-direction:column!important;align-items:center!important;justify-content:flex-start!important;width:100%!important;min-width:0!important;margin:0!important;padding:0 2px!important;gap:7px!important;color:var(--glb-text)!important;font-size:11px!important;font-weight:700!important;line-height:1.2!important;text-align:center!important;white-space:normal!important;opacity:1!important;filter:none!important}#glb-online-box .glb-step-text{display:block!important;width:100%!important;margin:0!important;padding:0!important;color:var(--glb-text)!important;font-size:11px!important;font-weight:700!important;line-height:1.2!important;text-align:center!important;white-space:normal!important;opacity:1!important}#glb-online-box .glb-step-number{flex:0 0 30px!important;width:30px!important;height:30px!important;min-width:30px!important;min-height:30px!important;color:#ffffff!important;font-size:12px!important;background:var(--glb-orange)!important;opacity:1!important}#glb-online-box .glb-form{width:100%!important;max-width:100%!important}#glb-online-box .glb-input{width:100%!important;height:46px!important;min-height:46px!important;padding:0 20px!important;font-size:14px!important}#glb-online-box .glb-category-details{width:100%!important;max-width:100%!important}#glb-online-box .glb-category-details>.glb-category-summary{width:100%!important;height:52px!important;min-height:52px!important;max-height:52px!important;padding:0 12px!important;font-size:15px!important}#glb-online-box .glb-category-details>.glb-category-dropdown{margin-top:10px!important}#glb-online-box .glb-category-option,#glb-online-box a.glb-category-item{height:42px!important;min-height:42px!important;max-height:42px!important}#glb-online-box a.glb-category-item{padding:0 13px!important;gap:10px!important;font-size:12px!important}#glb-online-box .glb-category-name{font-size:12px!important}}
    @media(max-width:380px){#glb-online-box .glb-title{font-size:31px!important}#glb-online-box .glb-step,#glb-online-box .glb-step-text{font-size:10px!important}#glb-online-box a.glb-category-item,#glb-online-box .glb-category-name{font-size:11px!important}}
`;

function OldToNewOfferModule({ config = {} }) {
    const [inputVal,       setInputVal]       = useState('');
    const [isOpen,         setIsOpen]         = useState(false);
    const [touched,        setTouched]        = useState(false);
    const [selectedCat,    setSelectedCat]    = useState('');
    const [step,           setStep]           = useState(1);
    const [allCategories,  setAllCategories]  = useState(GLB_OTN_CATEGORIES_PRESET);
    const dropdownRef                         = useRef(null);

    /* Fetch categories from backend API */
    useEffect(() => {
        let mounted = true;
        getCategories()
            .then(res => {
                if (!mounted || !Array.isArray(res) || res.length === 0) return;
                const rawList = flattenCategoryTree(res);
                const seen = new Set();
                const uniqueList = [];
                for (const item of rawList) {
                    const key = `${item.label.trim().toLowerCase()}_${item.topParentId}`;
                    if (!seen.has(key)) {
                        seen.add(key);
                        uniqueList.push(item);
                    }
                }
                if (uniqueList.length > 0) {
                    setAllCategories(uniqueList);
                }
            })
            .catch(err => {
                console.error('Failed to load API categories for OldToNew module:', err);
            });
        return () => { mounted = false; };
    }, []);

    /* inject CSS once into <head> */
    useEffect(() => {
        const sid = 'glb-otn-module-style';
        if (!document.getElementById(sid)) {
            const s = document.createElement('style');
            s.id  = sid;
            s.textContent = GLB_OTN_CSS;
            document.head.appendChild(s);
        }
    }, []);

    /* close on outside click / Escape while open */
    useEffect(() => {
        if (!isOpen) return;
        const onOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        const onEsc = (e) => { if (e.key === 'Escape') setIsOpen(false); };
        document.addEventListener('mousedown', onOutside);
        document.addEventListener('keydown',   onEsc);
        return () => {
            document.removeEventListener('mousedown', onOutside);
            document.removeEventListener('keydown',   onEsc);
        };
    }, [isOpen]);

    /* derived state */
    const showError = touched && !inputVal.trim();

    const queryClean = inputVal.trim().toLowerCase();
    const filtered = queryClean
        ? allCategories.filter(c =>
            c.search.includes(queryClean) ||
            c.label.toLowerCase().includes(queryClean)
          )
        : allCategories;

    /* handlers */
    const handleInputChange = (e) => {
        const v = e.target.value;
        setInputVal(v);
        setTouched(false);
        try {
            if (v.trim()) {
                sessionStorage.setItem('glb_old_product', v.trim());
                sessionStorage.setItem('glb_from_old_to_new', 'true');
            } else {
                sessionStorage.removeItem('glb_old_product');
                sessionStorage.removeItem('glb_from_old_to_new');
            }
        } catch (_) {}
        if (!v.trim()) { setStep(1); setIsOpen(false); }
    };

    const handleToggle = () => {
        if (!inputVal.trim()) { setTouched(true); return; }
        const opening = !isOpen;
        setIsOpen(opening);
        if (opening) setStep(2);
    };

    const handleCategoryClick = (e, cat) => {
        e.preventDefault();
        try {
            sessionStorage.setItem('glb_old_product', inputVal.trim());
            sessionStorage.setItem('glb_from_old_to_new', 'true');
            // Persist the top-level category's discount % for Checkout to use
            const discounts = config.categoryDiscounts || {};
            const pct = discounts[cat.topParentId] !== undefined ? discounts[cat.topParentId] : (discounts[cat.id] !== undefined ? discounts[cat.id] : 0);
            sessionStorage.setItem('glb_otn_discount', String(pct));
            const catIdToSave = String((cat && (cat.topParentId || cat.id)) || '');
            sessionStorage.setItem('glb_otn_category_id', catIdToSave);
        } catch (_) {}
        setSelectedCat(cat.label);
        setIsOpen(false);
        setStep(3);
        window.location.href = `/products?categoryId=${cat.id}`;
    };

    return (
        <div id="glb-online-box">
            <div className="glb-layout">

                {/* Title */}
                <div className="glb-title">
                    <span className="glb-title-part">Si funksionon</span>{' '}
                    <span className="glb-title-part">ONLINE?</span>
                </div>

                {/* Steps */}
                <div className="glb-steps-wrapper">
                    <div className="glb-steps">
                        {[
                            { n: 1, text: 'Vendos emrin e produktit te vjeter' },
                            { n: 2, text: 'Zgjidh produktin e ri' },
                            { n: 3, text: 'Shto produktin ne shporte' },
                        ].map(({ n, text }) => (
                            <div
                                key={n}
                                className={`glb-step${step === n ? ' active' : ''}${step > n ? ' completed' : ''}`}
                                data-glb-step={String(n)}
                            >
                                <span className="glb-step-number">{n}</span>
                                <span className="glb-step-text">{text}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Form */}
                <form
                    className="glb-form"
                    id="glb-product-form"
                    noValidate
                    onSubmit={e => e.preventDefault()}
                >
                    <input
                        type="text"
                        id="glb-product-input"
                        name="old_product"
                        className={`glb-input${showError ? ' invalid' : ''}`}
                        placeholder="Vendos emrin e produktit te vjeter  'Lavatrice'"
                        autoComplete="off"
                        required
                        value={inputVal}
                        onChange={handleInputChange}
                    />

                    <div
                        id="glb-product-error"
                        className={`glb-error${showError ? ' visible' : ''}`}
                    >
                        Ju lutem vendosni emrin e produktit te vjeter.
                    </div>

                    {/* Custom dropdown — pure React, no <details>/<summary> */}
                    <div
                        ref={dropdownRef}
                        className={`glb-category-details${isOpen ? ' is-open' : ''}`}
                        id="glb-category-details"
                    >
                        <button
                            type="button"
                            className="glb-category-summary"
                            id="glb-category-summary"
                            onClick={handleToggle}
                            aria-expanded={isOpen}
                            aria-haspopup="listbox"
                        >
                            <span id="glb-category-summary-text">
                                {selectedCat || 'Zgjidh produktin e ri'}
                            </span>
                            <span className="glb-button-arrow" aria-hidden="true" />
                        </button>

                        {/* Dropdown rendered only when open — filtering is just Array.filter(), no CSS hiding */}
                        {isOpen && (
                            <div className="glb-category-dropdown" role="listbox">
                                {filtered.length > 0
                                    ? filtered.map(cat => (
                                        <div className="glb-category-option" key={cat.id}>
                                            <a
                                                href={`/products?categoryId=${cat.id}`}
                                                className="glb-category-item"
                                                data-category={cat.search}
                                                role="option"
                                                aria-selected={false}
                                                onClick={e => handleCategoryClick(e, cat)}
                                            >
                                                <span className="glb-category-icon">{cat.icon}</span>
                                                <span className="glb-category-name">{cat.label}</span>
                                            </a>
                                        </div>
                                    ))
                                    : (
                                        <div style={{ padding: '12px 16px', color: '#666', fontSize: 13, lineHeight: 1.4 }}>
                                            Nuk u gjet asnjë kategori për &ldquo;{inputVal}&rdquo;
                                        </div>
                                    )
                                }
                            </div>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}

function HeadTagsModule({ config }) {
    useEffect(() => {
        if (!config || !config.code) return;

        try {
            const fragment = document.createRange().createContextualFragment(config.code);
            const nodes = Array.from(fragment.childNodes);
            nodes.forEach(node => document.head.appendChild(node));

            return () => {
                nodes.forEach(node => {
                    if (document.head.contains(node)) {
                        document.head.removeChild(node);
                    }
                });
            };
        } catch (err) {
            console.error('Error injecting head tags:', err);
        }
    }, [config]);

    return null;
}

function ImageTextButtonModule({ config }) {
    const uid = useMemo(() => Math.random().toString(36).substring(2, 9), []);
    if (!config) return null;
    const title = (config.title || '').trim();
    const textHtml = normalizeHtmlContent(config.text || '');
    const image = resolveAssetUrl(config.image || '');
    const imageAlt = (config.imageAlt || 'Image').trim();
    const imagePosition = config.imagePosition === 'right' ? 'right' : 'left';
    const buttonText = (config.buttonText || '').trim();
    const buttonLink = (config.buttonLink || '').trim();
    const buttonAlign = ['left', 'center', 'right'].includes(config.buttonAlign) ? config.buttonAlign : 'left';

    const ButtonWrap = ({ children }) => {
        if (!buttonLink) return <span style={{ opacity: 0.6, cursor: 'not-allowed' }}>{children}</span>;
        if (buttonLink.startsWith('http://') || buttonLink.startsWith('https://')) {
            return <a href={buttonLink} style={{ textDecoration: 'none' }}>{children}</a>;
        }
        return <Link to={buttonLink} style={{ textDecoration: 'none' }}>{children}</Link>;
    };

    const content = (
        <div style={{ flex: 1, minWidth: 0 }}>
            {title ? <h3 style={{ margin: '0 0 10px 0', fontSize: 24, fontWeight: 700, color: config?.titleColor || '#0f172a' }}>{title}</h3> : null}
            {textHtml ? <div className="ql-editor" style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 0, paddingBottom: 0 }} dangerouslySetInnerHTML={{ __html: textHtml }} /> : null}
            {buttonText ? (
                <div style={{ marginTop: 16, textAlign: buttonAlign }}>
                    <ButtonWrap>
                        <style>{`
                            .itb-btn-${uid} { background: ${config?.buttonBgColor || '#2563eb'} !important; color: ${config?.buttonTextColor || '#fff'} !important; }
                            .itb-btn-${uid}:hover { background: ${config?.buttonHoverBgColor || '#1d4ed8'} !important; color: ${config?.buttonHoverTextColor || '#fff'} !important; }
                        `}</style>
                        <span className={`itb-btn-${uid}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '10px 18px', borderRadius: 999, fontWeight: 600, fontSize: 14, transition: 'all 0.2s' }}>
                            {buttonText}
                        </span>
                    </ButtonWrap>
                </div>
            ) : null}
        </div>
    );

    const media = image ? (
        <div style={{ flex: 1, minWidth: 0 }}>
            <img src={image} alt={imageAlt} style={{ width: '100%', height: '100%', maxHeight: 360, objectFit: 'cover', borderRadius: 12 }} />
        </div>
    ) : null;

    return (
        <div style={{ maxWidth: 1540, margin: '24px auto', padding: '0 18px' }}>
            <div className="itb-stack" style={{ display: 'flex', gap: 24, alignItems: 'center', flexDirection: imagePosition === 'right' ? 'row-reverse' : 'row' }}>
                {media}
                {content}
            </div>
            <style>{`
                @media (max-width: 900px) {
                    .itb-stack { flex-direction: column !important; }
                }
            `}</style>
        </div>
    );
}

function ContactFormModule({ config }) {
    const title = (config && config.title) || 'Contact Us';
    const submitText = (config && config.submitText) || 'Dërgo Mesazhin';
    const configFields = config ? config.fields : undefined;
    const fileInputRef = useRef(null);
    const fields = useMemo(() => {
        return Array.isArray(configFields) ? configFields : [];
    }, [configFields]);

    const [values, setValues] = useState({});
    const [status, setStatus] = useState({ state: 'idle', message: '' });
    const [attachment, setAttachment] = useState(null);

    useEffect(() => {
        const next = {};
        fields.forEach((f, i) => {
            const name = f.name || `field_${i}`;
            next[name] = '';
        });
        setValues(next);
        setStatus({ state: 'idle', message: '' });
        setAttachment(null);
    }, [fields]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (status.state === 'submitting') return;

        const payloadFields = fields.map((f, i) => {
            const name = f.name || `field_${i}`;
            return {
                label: f.label || name,
                name,
                value: values[name] || ''
            };
        });

        try {
            setStatus({ state: 'submitting', message: '' });
            const formData = new FormData();
            formData.append('title', title || '');
            formData.append('fields', JSON.stringify(payloadFields));
            formData.append('pageUrl', typeof window !== 'undefined' ? window.location.href : '');
            formData.append('pageTitle', typeof document !== 'undefined' ? document.title : '');
            if (config?.receivingEmail) {
                formData.append('receivingEmail', config.receivingEmail);
            }
            if (attachment) formData.append('attachment', attachment);

            const res = await fetch(`${API_BASE_URL}/contact`, {
                method: 'POST',
                body: formData
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'Failed to send message');
            }

            setStatus({ state: 'success', message: 'Faleminderit! Mesazhi juaj u dërgua me sukses. Do t\'ju kontaktojmë së shpejti.' });
            const cleared = {};
            fields.forEach((f, i) => {
                const name = f.name || `field_${i}`;
                cleared[name] = '';
            });
            setValues(cleared);
            setAttachment(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (err) {
            setStatus({ state: 'error', message: err.message || 'Ndodhi një gabim gjatë dërgimit. Ju lutemi provoni përsëri.' });
        }
    };

    const uid = useMemo(() => Math.random().toString(36).substring(2, 9), []);
    return (
        <div style={{ maxWidth: 840, margin: '40px auto', padding: '0 20px', boxSizing: 'border-box' }}>
            <style>{`
                .cf-card-${uid} {
                    background: #ffffff;
                    border: 1.5px solid #e8edf2;
                    border-radius: 20px;
                    padding: 36px 32px;
                    box-shadow: 0 10px 30px rgba(0, 125, 165, 0.05), 0 1px 3px rgba(0, 0, 0, 0.02);
                }
                @media (max-width: 640px) {
                    .cf-card-${uid} {
                        padding: 24px 18px;
                        border-radius: 16px;
                    }
                }
                .cf-input-${uid} {
                    width: 100%;
                    padding: 12px 16px;
                    border-radius: 12px;
                    border: 1.5px solid #e2e8f0;
                    background: #f8fafc;
                    font-size: 14.5px;
                    color: #1e293b;
                    font-family: inherit;
                    box-sizing: border-box;
                    transition: all 0.2s ease;
                }
                .cf-input-${uid}:focus {
                    outline: none;
                    background: #ffffff;
                    border-color: #007da5;
                    box-shadow: 0 0 0 3.5px rgba(0, 125, 165, 0.12);
                }
                .cf-btn-${uid} { 
                    background: ${config?.buttonBgColor || '#007da5'} !important; 
                    color: ${config?.buttonTextColor || '#ffffff'} !important; 
                    height: 48px;
                    border-radius: 12px;
                    border: none;
                    font-size: 15px;
                    font-weight: 700;
                    letter-spacing: -0.2px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding: 0 28px;
                    cursor: pointer;
                    box-shadow: 0 4px 14px rgba(0, 125, 165, 0.25);
                    transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
                }
                .cf-btn-${uid}:hover { 
                    background: ${config?.buttonHoverBgColor || '#006080'} !important; 
                    color: ${config?.buttonHoverTextColor || '#ffffff'} !important; 
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(0, 125, 165, 0.35);
                }
                .cf-btn-${uid}:active {
                    transform: scale(0.98);
                }
            `}</style>
            <div className={`cf-card-${uid}`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, paddingBottom: 18, borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ width: 42, height: 42, borderRadius: 12, background: '#f0f8fb', border: '1.5px solid #d0e7f2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#007da5', flexShrink: 0 }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700, color: config?.titleColor || '#094859', letterSpacing: '-0.4px' }}>
                            {title}
                        </h2>
                    </div>
                </div>

                <form onSubmit={onSubmit} style={{ display: 'grid', gap: 16 }}>
                    {fields.map((f, i) => {
                        const key = f.id || `${f.name || 'field'}-${i}`;
                        const commonProps = {
                            name: f.name || `field_${i}`,
                            placeholder: f.placeholder || '',
                            required: !!f.required,
                            value: values[f.name || `field_${i}`] || '',
                            onChange: (e) => {
                                const key = f.name || `field_${i}`;
                                const val = e.target.value;
                                setValues(prev => ({ ...prev, [key]: val }));
                            },
                            className: `cf-input-${uid}`
                        };
                        return (
                            <div key={key} style={{ display: 'grid', gap: 6 }}>
                                {f.label ? (
                                    <label style={{ fontSize: 13.5, fontWeight: 600, color: '#334155' }}>
                                        {f.label} {f.required && <span style={{ color: '#ef4444' }}>*</span>}
                                    </label>
                                ) : null}
                                {f.type === 'textarea' ? (
                                    <textarea rows={4} {...commonProps} style={{ resize: 'vertical', minHeight: 100 }} />
                                ) : (
                                    <input type={f.type || 'text'} {...commonProps} />
                                )}
                            </div>
                        );
                    })}

                    <div style={{ display: 'grid', gap: 6 }}>
                        <label style={{ fontSize: 13.5, fontWeight: 600, color: '#334155' }}>Bashkëngjit Dokument (Opsionale)</label>
                        <div 
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            style={{ 
                                border: '1.5px dashed #cbd5e1', 
                                borderRadius: 12, 
                                background: '#f8fafc', 
                                padding: '14px 16px', 
                                cursor: 'pointer', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#007da5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                                </svg>
                                <span style={{ fontSize: 13.5, color: attachment ? '#007da5' : '#64748b', fontWeight: attachment ? 600 : 400 }}>
                                    {attachment ? attachment.name : 'Zgjidhni një skedar ose tërhiqeni këtu'}
                                </span>
                            </div>
                            {attachment ? (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setAttachment(null);
                                        if (fileInputRef.current) fileInputRef.current.value = '';
                                    }}
                                    style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                                >
                                    Fshi
                                </button>
                            ) : (
                                <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>Max 10MB</span>
                            )}
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            onChange={(e) => {
                                const file = e.target.files && e.target.files[0];
                                setAttachment(file || null);
                            }}
                            style={{ display: 'none' }}
                        />
                    </div>

                    <div style={{ marginTop: 8 }}>
                        <button
                            type="submit"
                            className={`cf-btn-${uid}`}
                            disabled={status.state === 'submitting'}
                            style={{ opacity: status.state === 'submitting' ? 0.7 : 1, cursor: status.state === 'submitting' ? 'not-allowed' : 'pointer' }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="22" y1="2" x2="11" y2="13" />
                                <polygon points="22 2 15 22 11 13 2 9 22 2" />
                            </svg>
                            <span>{status.state === 'submitting' ? 'Duke dërguar...' : submitText}</span>
                        </button>
                    </div>

                    {status.state === 'success' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', borderRadius: 12, padding: '12px 16px', fontSize: 14, fontWeight: 500 }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            <span>{status.message}</span>
                        </div>
                    ) : null}
                    {status.state === 'error' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 12, padding: '12px 16px', fontSize: 14, fontWeight: 500 }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <span>{status.message}</span>
                        </div>
                    ) : null}
                </form>
            </div>
        </div>
    );
}

function ButtonModule({ config }) {
    const uid = useMemo(() => Math.random().toString(36).substring(2, 9), []);
    if (!config) return null;
    const text = (config.text || '').trim();
    if (!text) return null;
    const link = (config.link || '').trim();
    const align = (config.align === 'center' || config.align === 'right') ? config.align : 'left';
    const variant = config.variant || 'primary';
    const newTab = !!config.newTab;

    // Add custom color logic
    const hasCustomColors = config.buttonBgColor || config.buttonTextColor;

    const baseStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px 22px',
        borderRadius: 999,
        fontWeight: 600,
        fontSize: 15,
        textDecoration: 'none',
        transition: 'all 0.2s ease',
        border: '1px solid transparent'
    };

    const variantStyles = {
        primary: { background: '#2563eb', color: '#fff', borderColor: '#2563eb' },
        secondary: { background: '#0f172a', color: '#fff', borderColor: '#0f172a' },
        outline: { background: '#fff', color: '#0f172a', borderColor: '#cbd5f5' },
    };

    const style = hasCustomColors ? baseStyle : { ...baseStyle, ...(variantStyles[variant] || variantStyles.primary) };
    const customClass = hasCustomColors ? `btn-mod-${uid}` : '';

    const Wrap = ({ children }) => {
        if (!link) {
            return (
                <span className={customClass} style={{ ...style, opacity: 0.6, cursor: 'not-allowed' }}>{children}</span>
            );
        }
        if (link.startsWith('http://') || link.startsWith('https://')) {
            return (
                <a href={link} className={customClass} target={newTab ? '_blank' : undefined} rel={newTab ? 'noreferrer' : undefined} style={style}>
                    {children}
                </a>
            );
        }
        return (
            <Link to={link} className={customClass} style={style}>
                {children}
            </Link>
        );
    };

    return (
        <div style={{ maxWidth: 1540, margin: '16px auto', padding: '0 18px', textAlign: align }}>
            {hasCustomColors && (
                <style>{`
                    .btn-mod-${uid} { background: ${config.buttonBgColor || '#2563eb'} !important; color: ${config.buttonTextColor || '#ffffff'} !important; border-color: ${config.buttonBgColor || '#2563eb'} !important; }
                    .btn-mod-${uid}:hover { background: ${config.buttonHoverBgColor || '#1d4ed8'} !important; color: ${config.buttonHoverTextColor || '#ffffff'} !important; border-color: ${config.buttonHoverBgColor || '#1d4ed8'} !important; }
                `}</style>
            )}
            <Wrap>{text}</Wrap>
        </div>
    );
}

function DividerModule({ config }) {
    if (!config) return null;
    const thickness = Number(config.thickness || 1);
    const width = Number(config.width || 100);
    const margin = Number(config.margin || 24);
    const color = (config.color || '#e2e8f0').trim();
    const style = (config.style || 'solid');

    const safeThickness = Number.isFinite(thickness) && thickness > 0 ? thickness : 1;
    const safeWidth = Number.isFinite(width) && width > 0 ? Math.min(100, width) : 100;
    const safeMargin = Number.isFinite(margin) && margin >= 0 ? margin : 24;

    return (
        <div style={{ maxWidth: 1540, margin: `${safeMargin}px auto`, padding: '0 18px' }}>
            <hr
                style={{
                    border: 'none',
                    borderTop: `${safeThickness}px ${style} ${color}`,
                    width: `${safeWidth}%`,
                    margin: '0 auto'
                }}
            />
        </div>
    );
}

function VideoModule({ config }) {
    if (!config) return null;
    const url = (config.url || '').trim();
    if (!url) return null;
    const title = (config.title || 'Video').trim();
    const aspect = config.aspect || '16:9';
    const autoplay = !!config.autoplay;
    const muted = !!config.muted;
    const controls = config.controls !== false;
    const loop = !!config.loop;

    const ratioMap = { '16:9': 56.25, '21:9': 42.857, '4:3': 75, '1:1': 100 };
    const paddingTop = ratioMap[aspect] || ratioMap['16:9'];

    const rawUrl = resolveAssetUrl(url);

    const parseYouTubeId = (u) => {
        const ytMatch = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/i);
        return ytMatch ? ytMatch[1] : null;
    };
    const parseVimeoId = (u) => {
        const vmMatch = u.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
        return vmMatch ? vmMatch[1] : null;
    };

    const ytId = parseYouTubeId(rawUrl);
    const vmId = parseVimeoId(rawUrl);

    let embedUrl = '';
    if (ytId) {
        const params = new URLSearchParams();
        params.set('autoplay', autoplay ? '1' : '0');
        params.set('mute', muted ? '1' : '0');
        params.set('controls', controls ? '1' : '0');
        params.set('loop', loop ? '1' : '0');
        if (loop) params.set('playlist', ytId);
        embedUrl = `https://www.youtube.com/embed/${ytId}?${params.toString()}`;
    } else if (vmId) {
        const params = new URLSearchParams();
        params.set('autoplay', autoplay ? '1' : '0');
        params.set('muted', muted ? '1' : '0');
        params.set('loop', loop ? '1' : '0');
        params.set('title', '0');
        params.set('byline', '0');
        params.set('portrait', '0');
        embedUrl = `https://player.vimeo.com/video/${vmId}?${params.toString()}`;
    }

    return (
        <div style={{ maxWidth: 1540, margin: '24px auto', padding: '0 18px' }}>
            <div style={{ position: 'relative', width: '100%', paddingTop: `${paddingTop}%`, background: '#0f172a', borderRadius: 12, overflow: 'hidden' }}>
                {embedUrl ? (
                    <iframe
                        title={title}
                        src={embedUrl}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                    />
                ) : (
                    <video
                        src={rawUrl}
                        title={title}
                        controls={controls}
                        muted={muted}
                        autoPlay={autoplay}
                        loop={loop}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                )}
            </div>
        </div>
    );
}

function HeadlineModule({ config }) {
    if (!config) return null;
    const text = (config.text || '').trim();
    if (!text) return null;
    const align = (config.align === 'center' || config.align === 'right' || config.align === 'left') ? config.align : 'left';
    const color = (config.color != null ? String(config.color) : '').trim();
    const fontSize = Number(config.fontSize || 0);
    return (
        <div style={{ maxWidth: 1540, margin: '24px auto', padding: '0 18px', textAlign: align }}>
            <h2
                className="text-2xl sm:text-2xl md:text-3xl font-semibold"
                style={{
                    color: color || '#094859',
                    margin: '0 0 16px 0',
                    ...(Number.isFinite(fontSize) && fontSize > 0 ? { fontSize } : {}),
                }}
            >
                {text}
            </h2>
        </div>
    );
}

function ProductsModule({ config, products }) {
    // Slider state
    const viewportRef = useRef(null);
    const trackRef = useRef(null);

    const defaultVisible = Number(config?.visible) || 6;
    const [currentVisible, setCurrentVisible] = useState(defaultVisible);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 480) setCurrentVisible(1);
            else if (window.innerWidth < 768) setCurrentVisible(2);
            else if (window.innerWidth < 1100) setCurrentVisible(3);
            else if (window.innerWidth < 1440) setCurrentVisible(4);
            else setCurrentVisible(Math.min(defaultVisible, 5));
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [defaultVisible]);

    const visible = currentVisible;
    const list = useMemo(() => products || [], [products]);
    const count = list.length;

    // Buffer to support infinite continuous scrolling
    const repeatCount = useMemo(() => {
        if (!count) return 0;
        return Math.max(5, Math.ceil((visible * 4) / count));
    }, [count, visible]);

    const displayProducts = useMemo(() => {
        if (!list || count === 0) return [];
        if (count <= 1) return list;
        const arr = [];
        for (let i = 0; i < repeatCount; i++) {
            arr.push(...list);
        }
        return arr;
    }, [list, count, repeatCount]);

    const totalRendered = displayProducts.length;

    const [idx, setIdx] = useState(() => (count > 1 ? Math.floor(Math.max(5, Math.ceil((defaultVisible * 4) / Math.max(1, count))) / 2) * count : 0));
    const [isTransitioning, setIsTransitioning] = useState(true);

    const dragStartXRef = useRef(0);
    const isDraggingRef = useRef(false);
    const hasMovedRef = useRef(false);

    // Sync index when products or visible count changes
    useEffect(() => {
        if (count > 1) {
            setIdx(Math.floor(repeatCount / 2) * count);
        } else {
            setIdx(0);
        }
    }, [count, repeatCount]);

    const handleTransitionEnd = () => {
        if (count <= 1) return;
        const middle = Math.floor(repeatCount / 2) * count;
        if (idx >= (repeatCount - 2) * count) {
            setIsTransitioning(false);
            setIdx(middle + (idx % count));
        } else if (idx <= count) {
            setIsTransitioning(false);
            setIdx(middle + (idx % count));
        }
    };

    const handlePrev = (e) => {
        e?.stopPropagation();
        if (count <= 1) return;
        const middle = Math.floor(repeatCount / 2) * count;

        if (idx <= count) {
            setIsTransitioning(false);
            const resetIndex = middle + (idx % count);
            setIdx(resetIndex);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setIsTransitioning(true);
                    setIdx(resetIndex - 1);
                });
            });
        } else {
            setIsTransitioning(true);
            setIdx((prev) => prev - 1);
        }
    };

    const handleNext = (e) => {
        e?.stopPropagation();
        if (count <= 1) return;
        const middle = Math.floor(repeatCount / 2) * count;
        const maxSafe = (repeatCount - 2) * count;

        if (idx >= maxSafe) {
            setIsTransitioning(false);
            const resetIndex = middle + (idx % count);
            setIdx(resetIndex);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setIsTransitioning(true);
                    setIdx(resetIndex + 1);
                });
            });
        } else {
            setIsTransitioning(true);
            setIdx((prev) => prev + 1);
        }
    };

    const onPointerDown = (e) => {
        if (count <= 1) return;
        const clientX = e.clientX ?? (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
        dragStartXRef.current = clientX;
        isDraggingRef.current = true;
        hasMovedRef.current = false;
    };

    const onPointerMove = (e) => {
        if (!isDraggingRef.current) return;
        const clientX = e.clientX ?? (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
        if (Math.abs(clientX - dragStartXRef.current) > 8) {
            hasMovedRef.current = true;
        }
    };

    const onPointerUp = (e) => {
        if (!isDraggingRef.current) return;
        isDraggingRef.current = false;
        const endX = e.clientX ?? (e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : dragStartXRef.current);
        const diffX = endX - dragStartXRef.current;
        if (Math.abs(diffX) >= 40) {
            if (diffX < 0) {
                handleNext();
            } else {
                handlePrev();
            }
        }
    };

    if (!products || products.length === 0) return null;

    return (
        <div className="px-6" style={{ width: '100%', maxWidth: '1540px', margin: '48px auto 28px', textAlign: 'start', paddingBottom: 24 }}>
            {config.title && <h2 className='text-2xl sm:text-2xl md:text-3xl font-semibold' style={{ marginTop: 0, marginBottom: 20, textAlign: 'start', color: config?.titleColor || '#094859' }}>{config.title}</h2>}
            <div style={{ position: 'relative', textAlign: 'center' }}>

                <div 
                    ref={viewportRef} 
                    style={{ overflow: 'hidden', borderRadius: 10, position: 'relative', zIndex: 1, willChange: 'transform', cursor: count > 1 ? 'grab' : 'default', touchAction: 'pan-y', userSelect: 'none' }}
                    onMouseDown={onPointerDown}
                    onMouseMove={onPointerMove}
                    onMouseUp={onPointerUp}
                    onMouseLeave={onPointerUp}
                    onTouchStart={onPointerDown}
                    onTouchMove={onPointerMove}
                    onTouchEnd={onPointerUp}
                    onTouchCancel={onPointerUp}
                >
                    <div
                        ref={trackRef}
                        onTransitionEnd={handleTransitionEnd}
                        onClickCapture={(e) => {
                            if (hasMovedRef.current) {
                                e.preventDefault();
                                e.stopPropagation();
                            }
                        }}
                        style={{
                            marginTop: '10px',
                            display: 'flex',
                            transition: isTransitioning ? 'transform 420ms ease' : 'none',
                            width: totalRendered > 0 ? `${(100 * totalRendered) / visible}%` : "100%",
                            transform: `translateX(${totalRendered > 0 ? -((idx * 100) / totalRendered) : 0}%)`,
                            willChange: 'transform'
                        }}
                    >
                        {displayProducts.map((p, i) => (
                            <div key={`${p.id || 'prod'}-${i}`} style={{ width: totalRendered > 0 ? `${100 / totalRendered}%` : "100%", boxSizing: 'border-box', padding: 8 }}>
                                <ProductCard product={p} />
                            </div>
                        ))}
                    </div>
                </div>
                {count > 1 && (
                    <div style={{ position: 'absolute', right: 20, bottom: -32, display: 'flex', gap: 8, zIndex: 10 }}>
                        <button
                            type="button"
                            className="glb-slider-nav-btn"
                            aria-label="Previous products"
                            onClick={handlePrev}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                        </button>
                        <button
                            type="button"
                            className="glb-slider-nav-btn"
                            aria-label="Next products"
                            onClick={handleNext}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                        </button>
                    </div>
                )}
            </div>

        </div>
    );
}


function FeaturePromoModule({ config }) {
    const promoTitle = (config && config.promoTitle) || 'Më e mira për shtëpinë';
    const promoSubtitle = (config && config.promoSubtitle) || 'teknologjia AEG';
    const promoText = (config && config.promoText) || '';
    const promoImage = ensureHttpsIfNeeded((config && config.promoImage) || bannerImg2);
    const promoImageAlt = (config && config.promoImageAlt) || 'img_2';

    const promoButtonText = (config && config.promoButtonText) || '';
    const rawLink = (config && config.promoButtonLink) || '';
    const promoButtonLink = rawLink === '/blogs' || !rawLink ? (rawLink === '/blogs' ? '/blog' : '') : (rawLink.startsWith('/blogs/') ? rawLink.replace('/blogs/', '/blog/') : rawLink);
    const uid = useMemo(() => Math.random().toString(36).substring(2, 9), []);

    return (
        <section style={{ width: '100%', marginTop: 82, boxSizing: 'border-box' }}>
            <div className="flex items-center justify-center mb-10">
                <div className="group flex flex-col-reverse md:flex-row w-[94%] sm:w-[90%] lg:w-[80%] lg:h-[450px] md:min-h-[400px] uppercase rounded-3xl border-2 border-solid border-gray-300 overflow-hidden">
                    <div className="w-full md:w-[40%] p-6 sm:p-10 overflow-hidden flex flex-col justify-center">
                        <div className="flex flex-col h-full">
                            <div>
                                <h1 className="text-xl sm:text-2xl md:text-2xl lg:text-3xl xl:text-3xl py-4" style={{ color: config?.titleColor || 'inherit', fontWeight: config?.titleBold ? 'bold' : 'normal', letterSpacing: '-0.6px' }}>{promoTitle}</h1>
                                <h2 className="text-xl sm:text-2xl md:text-2xl lg:text-3xl xl:text-3xl" style={{ color: config?.subtitleColor || 'inherit', fontWeight: config?.subtitleBold ? 'bold' : 'normal', letterSpacing: '-0.5px' }}>{promoSubtitle}</h2>
                            </div>
                            <div className="flex-1 mt-4">
                                {promoText ? (
                                    <p className="normal-case text-sm md:text-base lg:text-base xl:text-lg text-black-600 !pr-8 leading-loose" style={{ color: config?.textColor || 'inherit', fontWeight: config?.textBold ? 'bold' : 450, letterSpacing: '-0.2px' }}>{promoText}</p>
                                ) : null}
                            </div>
                            {promoButtonText && (
                                <div className="mt-6">
                                    <style>{`
                                        .fp-btn-${uid} { background: #007da5; color: #ffffff; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); letter-spacing: -0.25px; }
                                        .fp-btn-${uid}:hover { background: #f48d21; color: #ffffff; transform: translateY(-3px) scale(1.03); box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05); }
                                    `}</style>
                                    <Link to={promoButtonLink || '#'} className={`fp-btn-${uid} inline-block px-8 py-3 rounded-xl font-bold text-sm transition-colors`} style={{ letterSpacing: '-0.25px' }}>
                                        {promoButtonText}
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="w-full md:w-[60%] h-[250px] sm:h-[350px] md:h-auto bg-[#f9fafb] overflow-hidden">
                        <img className="w-full h-full object-cover block" src={promoImage} alt={promoImageAlt} style={{ transition: 'transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)', willChange: 'transform' }} />
                    </div>
                </div>
            </div>
        </section>
    );
}

function PromoStripModule({ config }) {
    if (!config) return null;

    const text = config.text || '';
    const description = config.description || '';
    const linkText = config.linkText || '';
    const linkUrl = config.linkUrl || '';
    const imageDesktop = ensureHttpsIfNeeded(config.imageDesktop || config.image || bannerImg);
    const imageMobile = ensureHttpsIfNeeded(config.imageMobile || config.image || '');

    return (
        <div className="hp-promo-strip relative" style={{ width: '100%', marginTop: 82, background: '#f1f1f1ff', boxShadow: '0 6px 20px rgba(0,0,0,0.06)' }}>
            <div style={{ width: '100%', boxSizing: 'border-box' }}>
                <div className="flex flex-row items-center min-h-[140px] md:h-44 overflow-hidden">
                    <div className="w-[72%] sm:w-[65%] lg:w-[60%] flex flex-col justify-center pl-[12%] pr-[5%] py-4">
                        <div className="font-normal text-lg sm:text-xl md:text-2xl lg:text-3xl leading-tight" style={{ color: config?.titleColor || 'inherit' }}>{text}</div>
                        {description ? <p className="text-gray-600 text-[10px] sm:text-xs md:text-sm py-1 line-clamp-2">{description}</p> : null}
                        {linkText && linkUrl && (
                            <Link className="group text-left mt-2 inline-block" to={linkUrl} style={{ fontSize: 13 }}>
                                <span className="text-[#f48d21] font-bold mr-1">&gt;</span>
                                <span className="group-hover:underline group-hover:!text-[#007da5] font-semibold transition-colors" style={{ color: config?.linkColor || '#1a1a1a' }}>{linkText}</span>
                            </Link>
                        )}
                    </div>
                    <div className="w-[28%] sm:w-[35%] lg:w-[40%] h-full bg-[#f1f1f1ff]">
                        <picture>
                            {imageMobile ? <source media="(max-width: 768px)" srcSet={imageMobile} /> : null}
                            <img className="w-full h-full object-cover block" src={imageDesktop} alt="banner-img" />
                        </picture>
                    </div>
                </div>
            </div>
        </div>
    );
}

function PopupModule({ config }) {
    const delayMs = typeof config?.delay === 'number' && config.delay > 0 ? config.delay * 1000 : 0;
    const [open, setOpen] = useState(delayMs === 0);

    useEffect(() => {
        if (delayMs > 0) {
            const timer = setTimeout(() => {
                setOpen(true);
            }, delayMs);
            return () => clearTimeout(timer);
        }
    }, [delayMs]);

    if (!config) return null;

    const showBanner = config.showBanner !== false;
    const showText = config.showText !== false;
    const title = (config.title || '').trim();
    const text = config.text || '';
    const imageDesktop = ensureHttpsIfNeeded(config.imageDesktop || config.image || '');
    const imageMobile = ensureHttpsIfNeeded(config.imageMobile || config.image || '');

    const hasBanner = showBanner && (imageDesktop || imageMobile);
    const hasText = showText && (title || text);

    if (!hasBanner && !hasText) return null;
    if (!open) return null;

    return (
        <div
            className="pr-popup-overlay"
            role="dialog"
            aria-modal="true"
            onClick={() => setOpen(false)}
        >
            <style>{`
                .pr-popup-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(15, 23, 42, 0.55);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 16px;
                    z-index: 9999;
                }
                .pr-popup-card {
                    width: min(720px, 94vw);
                    background: #fff;
                    border-radius: 14px;
                    overflow: hidden;
                    box-shadow: 0 20px 50px rgba(0,0,0,0.2);
                    position: relative;
                    max-height: 90vh;
                    display: flex;
                    flex-direction: column;
                }
                .pr-popup-close {
                    position: absolute;
                    top: 10px;
                    right: 10px;
                    width: 34px;
                    height: 34px;
                    border-radius: 999px;
                    border: none;
                    background: rgba(15,23,42,0.08);
                    color: #0f172a;
                    font-size: 20px;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                }
                .pr-popup-banner {
                    width: 100%;
                    background: #f8fafc;
                }
                .pr-popup-banner img {
                    width: 100%;
                    height: auto;
                    display: block;
                    object-fit: cover;
                }
                .pr-popup-content {
                    padding: 18px 20px 22px;
                    overflow-y: auto;
                }
                .pr-popup-title {
                    font-size: 20px;
                    font-weight: 700;
                    color: #0f172a;
                    margin-bottom: 10px;
                }
                .pr-popup-text {
                    font-size: 15px;
                    line-height: 1.6;
                    color: #334155;
                }
                @media (max-width: 640px) {
                    .pr-popup-content { padding: 14px 16px 18px; }
                    .pr-popup-title { font-size: 18px; }
                    .pr-popup-text { font-size: 14px; }
                }
            `}</style>
            <div className="pr-popup-card" onClick={(e) => e.stopPropagation()}>
                <button className="pr-popup-close" type="button" aria-label="Close popup" onClick={() => setOpen(false)}>×</button>
                {hasBanner ? (
                    <div className="pr-popup-banner">
                        {(() => {
                            const link = config?.link || '';
                            const bannerContent = (
                                <picture>
                                    {imageMobile ? <source media="(max-width: 768px)" srcSet={imageMobile} /> : null}
                                    <img src={imageDesktop || imageMobile} alt={title || 'Popup banner'} />
                                </picture>
                            );
                            if (!link) return bannerContent;
                            const isExternal = link.startsWith('http://') || link.startsWith('https://');
                            if (isExternal) {
                                return <a href={link} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>{bannerContent}</a>;
                            }
                            return <Link to={link} style={{ display: 'block' }}>{bannerContent}</Link>;
                        })()}
                    </div>
                ) : null}
                {hasText ? (
                    <div className="pr-popup-content">
                        {title ? <div className="pr-popup-title" style={{ color: config?.titleColor || '#0f172a' }}>{title}</div> : null}
                        {text ? <div className="pr-popup-text" dangerouslySetInnerHTML={{ __html: text }} /> : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function CategoriesModule({ categories, config }) {
    if (!categories || categories.length === 0) return null;

    return (
        <section className="categories-section" style={{ width: '100%', marginTop: 54, boxSizing: 'border-box' }}>
            <div style={{ width: '100%', maxWidth: '1520px', margin: '0 auto', padding: '0 20px', boxSizing: 'border-box' }}>
                {config.title && (
                    <h2
                        className="text-2xl sm:text-2xl md:text-3xl font-semibold"
                        style={{ margin: '0 0 20px 0', color: config?.titleColor || '#094859' }}
                    >
                        {config.title}
                    </h2>
                )}
                <style>{`
                    .pr-cats-grid { 
                        display: grid; 
                        grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); 
                        gap: 28px; 
                    }
                    @media (max-width: 640px) {
                        .pr-cats-grid { 
                            grid-template-columns: repeat(2, 1fr); 
                            gap: 14px; 
                        }
                        .pr-cat-card {
                            height: 220px !important;
                            border-radius: 14px !important;
                        }
                        .pr-cat-img-wrapper img {
                            padding: 12px !important;
                        }
                        .pr-cat-info {
                            height: 48px !important;
                            padding: 0 10px !important;
                        }
                        .pr-cat-name {
                            font-size: 0.95rem !important;
                        }
                    }
                    .pr-cat-card {
                        position: relative;
                        background: #fff;
                        border-radius: 18px;
                        overflow: hidden;
                        transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
                        border: 1.5px solid #f1f5f9;
                        height: 265px; /* Desktop static height */
                        display: flex;
                        flex-direction: column;
                        font-weight: 500;
                    }
                    .pr-cat-link {
                        text-decoration: none;
                        color: inherit;
                        height: 100%;
                        display: flex;
                        flex-direction: column;
                    }
                    .pr-cat-img-wrapper {
                        flex: 1;
                        overflow: hidden;
                        position: relative;
                        background: #f8fafc;
                    }
                    .pr-cat-img-wrapper img {
                        width: 100%;
                        height: 100%;
                        object-fit: contain; /* Professional look for product categories */
                        padding: 15px;
                        transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
                    }
                    .pr-cat-overlay {
                        position: absolute;
                        inset: 0;
                        background: linear-gradient(to top, rgba(0,0,0,0.04) 0%, transparent 60%);
                        opacity: 0.3;
                        transition: opacity 0.4s ease;
                    }
                    .pr-cat-info {
                        height: 54px; /* Static height for text area */
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 0 16px;
                        background: #fff;
                        border-top: 1px solid #f1f5f9;
                        transition: all 0.3s ease;
                    }
                    .pr-cat-name {
                        font-weight: 600;
                        font-size: 1.05rem;
                        color: #1e293b;
                        transition: color 0.3s ease;
                        text-align: center;
                    }
                    @media (hover: hover) {
                        .pr-cat-card:hover {
                            transform: translateY(-6px);
                            box-shadow: 0 12px 28px rgba(0, 125, 165, 0.1);
                            border-color: #007da5;
                        }
                        .pr-cat-card:hover .pr-cat-img-wrapper img {
                            transform: scale(1.06);
                        }
                        .pr-cat-card:hover .pr-cat-info {
                            background: #f0f9ff;
                        }
                        .pr-cat-card:hover .pr-cat-name {
                            color: #007da5;
                        }
                    }
                `}</style>

                <div className="pr-cats-grid">
                    {categories.map(cat => (
                        <div key={cat.id} className="pr-cat-card">
                            <Link to={`/products?category=${encodeURIComponent(cat.name)}`} className="pr-cat-link">
                                <div className="pr-cat-img-wrapper">
                                    {(() => {
                                        const customImg = config.customImages && config.customImages[cat.name];
                                        const finalImg = customImg || cat.image;

                                        if (finalImg) {
                                            const src = ensureHttpsIfNeeded(finalImg.startsWith('http') ? finalImg : `${serverBase}${finalImg.startsWith('/') ? finalImg : '/' + finalImg}`);
                                            return <img src={src} alt={cat.name} />;
                                        } else {
                                            return <div style={{ width: '100%', height: '100%', background: '#f8fafc' }} />;
                                        }
                                    })()}
                                    <div className="pr-cat-overlay" />
                                </div>
                                <div className="pr-cat-info">
                                    <div className="pr-cat-name">{cat.name}</div>
                                </div>
                            </Link>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}


function BlogsModule({ posts, config }) {
    if (!posts || !posts.length) return null;

    let displayPosts = posts;
    const selectedCats = (config && Array.isArray(config.categories)) ? config.categories.filter(Boolean) : [];
    if (selectedCats.length) {
        displayPosts = displayPosts.filter((p) => selectedCats.includes(p.category));
    }
    const limit = (config && config.limit) ? Number(config.limit) : 0;
    if (limit) displayPosts = displayPosts.slice(0, limit);

    if (!displayPosts.length) return null;

    return (
        <div style={{ width: '100%', marginTop: 48 }}>
            <div style={{ maxWidth: 1540, margin: '0 auto', padding: '0 18px', boxSizing: 'border-box', marginBottom: '36px' }}>
                {config.title && (
                    <h2 className="text-2xl sm:text-2xl md:text-3xl font-semibold" style={{ marginTop: 0, marginBottom: 20, color: config?.titleColor || '#094859' }}>
                        {config.title}
                    </h2>
                )}
                <div className="cards-wrapper">
                    {displayPosts.map((post) => {
                        const imgUrl = post.image ? resolveAssetUrl(post.image) : 'https://picsum.photos/seed/blog' + post.id + '/800/600';
                        const postCats = getPostCategories(post);
                        return (
                            <Link key={post.id} className="mac-blog-card" to={`/blog/${post.id}`}>
                                <div className="mac-blog-img-wrap">
                                    <img src={imgUrl} alt={post.title} loading="lazy" />
                                </div>
                                <div className="mac-blog-content">
                                    <div className="mac-blog-meta" style={{ flexWrap: 'wrap', gap: 6 }}>
                                        {postCats.map(cat => (
                                            <span key={cat} className="mac-blog-tag">{cat}</span>
                                        ))}
                                        {post.createdAt && <span className="mac-blog-date">{new Date(post.createdAt).toLocaleDateString()}</span>}
                                    </div>
                                    <h3 className="mac-blog-title">{post.title}</h3>
                                    <p className="mac-blog-excerpt">{post.excerpt || (post.content ? post.content.replace(/<[^>]+>/g, '').slice(0, 110) + '…' : '')}</p>
                                    <div className="mac-blog-footer">
                                        <span>Lexo artikullin</span>
                                        <span>→</span>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function BrandsModule({ brands, config }) {
    if (!brands || !brands.length) return null;


    // Reuse simple horizontal scroll
    return (
        <section style={{ width: '100%', marginTop: 24, marginBottom: 40, padding: '0', boxSizing: 'border-box' }}>
            {/*  END SLIDER  */}
            <div className="text-3xl ">
                <BrandSlider brands={brands} config={config} serverBase={serverBase} />

            </div>
            {/* OLD END SLIDER */}
            {/* {config.title && <h2 className="px-[10%] text-3xl font-semibold mt-[5%] mb-[3rem]" style={{ }}>{config.title}</h2>}
 
 
            <div style={{ width: '70%', margin: '0 auto', padding: '0 18px', boxSizing: 'border-box', overflowX: 'auto', display: 'flex', gap: 40, paddingBottom: 20 }}>
                {brands.map((b) => {
                    const logoPath = b.image ? (b.image.startsWith('http') ? b.image : `${serverBase}${b.image.startsWith('/') ? b.image : '/' + b.image}`) : `https://picsum.photos/seed/${encodeURIComponent(b.name)}/180/100`;
                    return (
                        <div  key={b.name} style={{ flex: '0 0 auto', width: 200,  border: '1px solid #eee', padding:'0 0 20px 0' }}>
                            
                            <Link to={`/products?brand=${encodeURIComponent(b.name)}`} style={{ textDecoration: 'none' }}>
                            
                                <div style={{ width: 200, height: 200, display: 'flex', gap:'10px', alignItems: 'center', justifyContent: 'center' }}>
                                    <img src={logoPath} alt={b.name} style={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain' }} />
                                    
                                </div>
                                <div style={{ textAlign: 'center', marginTop: 6, fontSize: 13, fontWeight: 600, color: '#333' }}>{b.name}</div>
                                
                            </Link>
                           
                        </div>
                    )
                })}
            </div> */}
        </section>
    )
}


// Announcement Module - matches static header design
function AnnouncementModule({ config }) {
    const [activeIndex, setActiveIndex] = useState(0);

    // Fallback securely if config.items isn't present
    const items = config?.items || (config?.text ? [{ text: config.text, linkText: config.linkText, linkUrl: config.linkUrl }] : []);
    const speedMs = (config?.speed || 5) * 1000;

    useEffect(() => {
        if (items.length <= 1) return;
        const interval = setInterval(() => {
            setActiveIndex(prev => (prev + 1) % items.length);
        }, speedMs);
        return () => clearInterval(interval);
    }, [items.length, speedMs]);

    if (!items.length) return null;

    const currentItem = items[activeIndex] || items[0];
    const htmlText = normalizeHtmlContent(currentItem.text || '');

    return (
        <div style={{ width: '100%', background: '#f1f1f1', padding: '24px 0' }}>
            <style>{`
                .pr-announcement-text p { display: inline; margin: 0; }
                @keyframes announceFadeIn {
                    from { opacity: 0; transform: translateY(4px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
            <div style={{ maxWidth: 1680, margin: '0 auto', padding: '0 5.3%' }}>
                <div style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'start', width: '100%', textAlign: 'center' }}>
                    <div className="w-8 h-8 sm:w-12 sm:h-12 flex-none rounded-full border border-gray-300 flex items-center justify-center bg-white text-lg sm:text-xl transition-all">!</div>
                    <div key={activeIndex} className="text-gray-800 tracking-wider text-xs sm:text-sm md:text-base uppercase font-normal text-left pr-announcement-text" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', rowGap: '4px', animation: 'announceFadeIn 0.5s ease-out forwards' }}>
                        <span dangerouslySetInnerHTML={{ __html: htmlText }} />
                        {currentItem.linkText && currentItem.linkUrl && (
                            <Link to={currentItem.linkUrl} className="text-gray-800 underline ml-2 font-semibold">
                                {currentItem.linkText}
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}


// OFFER MODULES (REVISED)

function OfferHeroModule({ config }) {
    if (!config) return null;
    const { titlePart1, titlePart2, titlePart3, image } = config;
    const imgUrl = resolveAssetUrl(image);

    return (
        <section style={{ width: '100%', background: '#F8F9FA', padding: '40px 0', overflow: 'hidden' }}>
            <div style={{ maxWidth: 1440, margin: '0 auto', padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: 'row', position: 'relative', minHeight: 400 }}>
                {/* Text Left */}
                <div style={{ flex: 1, zIndex: 2 }}>
                    <h1 style={{ fontSize: 'clamp(3rem, 6vw, 5rem)', lineHeight: 1.1, fontWeight: 700, color: config?.titleColor || '#003b49', margin: 0 }}>
                        {titlePart1} <br />
                        {titlePart2} <span style={{ color: '#F97316' }}>{titlePart3}</span>
                    </h1>
                </div>

                {/* Image Right */}
                <div style={{ flex: 1.2, display: 'flex', justifyContent: 'flex-end', zIndex: 1 }}>
                    {imgUrl ? (
                        <img src={imgUrl} alt="Offer Hero" style={{ maxWidth: '100%', maxHeight: 500, objectFit: 'contain' }} />
                    ) : (
                        // Placeholder for the "product cloud" if no image
                        <div style={{ width: '100%', height: 400, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 20 }}>
                            <span style={{ color: '#aaa' }}>Product Image Cluster</span>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

function OfferFeaturedModule({ config }) {
    const uid = useMemo(() => Math.random().toString(36).substring(2, 9), []);
    if (!config) return null;
    const title = config.title || 'OFERTA SOT';
    // Use configured categories or fallback to defaults
    const links = (config.categories && config.categories.length) ? config.categories : [
        { label: 'Elektroshtëpiake', link: '/products?category=Elektroshtëpiake' },
        { label: 'Televizor', link: '/products?category=Televizor' },
        { label: 'IT & Mobile', link: '/products?category=IT%20&%20Mobile' }
    ];
    // Ensure we have at least empty objects for cards if not provided
    const cards = (config.cards && config.cards.length) ? config.cards : [{}, {}, {}];

    const Wrap = ({ link, children }) => {
        if (!link) return <>{children}</>;
        return <Link to={link || '#'} style={{ textDecoration: 'none', color: 'inherit' }}>{children}</Link>;
    };

    return (
        <section style={{ width: '100%', marginTop: 40, marginBottom: 40 }}>
            <div style={{ maxWidth: 1440, margin: '0 auto', padding: '0 20px' }}>
                {/* Header / Tabs */}
                <div style={{ display: 'flex', alignItems: 'stretch', marginBottom: 30, flexWrap: 'wrap', gap: 0, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    {/* Title Box */}
                    <div style={{
                        border: '1px solid #ddd',
                        padding: '12px 30px',
                        fontWeight: 800,
                        fontSize: '1.2rem',
                        color: config.titleColor || '#003b49',
                        display: 'flex',
                        alignItems: 'center',
                        background: '#fff',
                        textTransform: 'uppercase',
                        minWidth: 180,
                        justifyContent: 'center'
                    }}>
                        {title}
                    </div>
                    {/* Hardcoded Links - rendered as boxes adjacent to title */}
                    <div style={{ display: 'flex', flex: 1, overflowX: 'auto' }}>
                        {links.map((link, i) => (
                            <Link key={i} to={link.link} style={{
                                flex: 1,
                                border: '1px solid #ddd',
                                borderLeft: 'none',
                                padding: '12px 20px',
                                fontSize: '1.0rem',
                                fontWeight: 600,
                                color: '#555',
                                whiteSpace: 'nowrap',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: '#fff',
                                minWidth: 140
                            }}>
                                {link.label}
                            </Link>
                        ))}
                    </div>
                </div>

                {/* Cards Grid - Exactly 3 columns */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 30 }}>
                    {cards.slice(0, 3).map((card, i) => (
                        <div key={i} style={{
                            border: '4px solid #D6E4E8', // Light blue thick border
                            borderRadius: 30,
                            padding: 20,
                            background: '#F0F9FF',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16,
                            position: 'relative',
                            minHeight: 320,
                            alignItems: 'center'
                        }}>
                            {/* Image Area */}
                            <div style={{ width: '100%', height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                                {card.image ? (
                                    <img src={resolveAssetUrl(card.image)} alt="Offer" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                ) : (
                                    <div style={{ width: 140, height: 140, background: '#e2e8f0', borderRadius: '50%' }} />
                                )}

                            </div>

                            {/* Button */}
                            <div style={{ marginTop: 'auto', width: '100%' }}>
                                <Wrap link={card.link}>
                                    <button className={`ofeat-btn-${uid} transition-colors`} style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: 8,
                                        fontWeight: 700,
                                        fontSize: '1.2rem',
                                        border: 'none',
                                        cursor: 'pointer',
                                        textTransform: 'uppercase'
                                    }}>
                                        {card.buttonText || 'BLI TANI'}
                                    </button>
                                </Wrap>
                            </div>
                        </div>
                    ))}
                </div>
                <style>{`
                    .ofeat-btn-${uid} {
                        background: ${config?.buttonBgColor || '#F97316'} !important;
                        color: ${config?.buttonTextColor || '#ffffff'} !important;
                    }
                    .ofeat-btn-${uid}:hover {
                        background: ${config?.buttonHoverBgColor || '#ea580c'} !important;
                        color: ${config?.buttonHoverTextColor || '#ffffff'} !important;
                    }
                    @media (max-width: 900px) {
                        div[style*="grid-template-columns: repeat(3, 1fr)"] {
                            grid-template-columns: 1fr !important;
                        }
                    }
                `}</style>
            </div>
        </section>
    );
}

function OfferCatalogsModule({ config }) {
    const title = config?.title || 'KATALOGU AKTIV';
    const sticker = config?.sticker || 'VENDOSEN KATALOGJET';
    const items = config?.items || [];

    return (
        <section style={{ width: '100%', margin: '60px 0', textAlign: 'center' }}>
            <div style={{ maxWidth: 1540, margin: '0 auto', padding: '0 20px', position: 'relative' }}>
                <h2 style={{ fontSize: '2rem', fontWeight: 700, color: config?.titleColor || '#003b49', textTransform: 'uppercase', marginBottom: 40 }}>{title}</h2>

                {/* Sticker/Arrow hint */}
                <div style={{ position: 'absolute', top: 0, right: '15%', transform: 'rotate(-5deg)', fontFamily: 'cursive', color: '#333' }}>
                    {sticker} &darr;
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', gap: 40, flexWrap: 'wrap' }}>
                    {(items.length ? items : [{ dates: '24-31 JANAR' }, { dates: '26 JANAR - 1 SHKURT' }]).map((item, i) => (
                        <div key={i} style={{ width: 320, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{ width: '100%', height: 220, background: '#ccc', borderRadius: 12, marginBottom: 16, overflow: 'hidden', boxShadow: '0 10px 20px rgba(0,0,0,0.15)' }}>
                                {/* Placeholder for Catalog Cover */}
                                <div style={{ width: '100%', height: '100%', background: 'linear-gradient(45deg, #bbb, #ddd)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', fontWeight: 600 }}>
                                    Katalogu Online
                                </div>
                            </div>
                            <div style={{ fontWeight: 600, color: '#444' }}>{item.dates}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function OfferNeedsModule({ config }) {
    const title = config?.title || 'ZGJIDHJE SIPAS NEVOJËS';
    const items = config?.items || [];
    const uid = useMemo(() => Math.random().toString(36).substring(2, 9), []);

    const Wrap = ({ link, children }) => {
        if (!link) return <>{children}</>;
        return <Link to={link || '#'} style={{ textDecoration: 'none', color: 'inherit' }}>{children}</Link>;
    };

    return (
        <section style={{ width: '100%', marginBottom: 60, textAlign: 'center' }}>
            <h2 style={{ fontSize: '2rem', fontWeight: 700, color: config?.titleColor || '#003b49', textTransform: 'uppercase', marginBottom: 30 }}>{title}</h2>
            <style>{`
                .on-btn-${uid} {
                    background: ${config?.buttonBgColor || '#E2E8F0'} !important;
                    color: ${config?.buttonTextColor || '#475569'} !important;
                }
                .on-btn-${uid}:hover {
                    background: ${config?.buttonHoverBgColor || '#F97316'} !important;
                    color: ${config?.buttonHoverTextColor || '#ffffff'} !important;
                }
            `}</style>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
                {(items.length ? items : [{ label: 'Për shtëpinë' }, { label: 'Për familjen' }, { label: 'Për mua' }]).map((item, i) => (
                    <Wrap key={i} link={item.link}>
                        <button className={`on-btn-${uid} transition-colors`} style={{
                            padding: '14px 40px',
                            borderRadius: 30,
                            border: 'none',
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            minWidth: 160
                        }}>
                            {item.label}
                        </button>
                    </Wrap>
                ))}
            </div>
        </section>
    );
}

function OfferFooterModule({ config }) {
    const text = config?.text || 'OFERTA NUK PRET. AS NEVOJA JOTE';
    const btnText = config?.buttonText || 'Gjej dyqanin afër meje';
    const uid = useMemo(() => Math.random().toString(36).substring(2, 9), []);
    return (
        <section style={{ width: '100%', padding: '60px 20px', textAlign: 'center' }}>
            <h2 style={{ fontSize: '1.8rem', fontWeight: 800, color: config?.titleColor || '#003b49', textTransform: 'uppercase', marginBottom: 24, letterSpacing: 1 }}>{text}</h2>
            <style>{`
                .of-btn-${uid} {
                    background: ${config?.buttonBgColor || '#0ea5e9'} !important;
                    color: ${config?.buttonTextColor || '#fff'} !important;
                }
                .of-btn-${uid}:hover {
                    background: ${config?.buttonHoverBgColor || '#0284c7'} !important;
                    color: ${config?.buttonHoverTextColor || '#fff'} !important;
                }
            `}</style>
            {config?.buttonLink ? (
                <Link to={config.buttonLink}>
                    <button className={`of-btn-${uid}`} style={{
                        padding: '16px 32px',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                    }}>
                        {btnText}
                    </button>
                </Link>
            ) : (
                <button className={`of-btn-${uid}`} style={{
                    padding: '16px 32px',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                }}>
                    {btnText}
                </button>
            )}
        </section>
    );
}

function MetodatPageseModule({ config }) {
    const items = config?.items || [];
    const titleColor = config?.titleColor || '#002f4a';
    const descColor = config?.descColor || '#475569';
    const dotColor = config?.dotColor || '#9ca3af';
    const bgColor = config?.bgColor || '#f8fafc';

    // Track which items are expanded (allow multiple to be open, or just one. Let's allow multiple for typical accordion behavior, or single. We'll do single active index for standard accordion).
    const [activeIndex, setActiveIndex] = useState(null);

    return (
        <section style={{ width: '100%', margin: '40px 0', fontFamily: 'system-ui, sans-serif' }}>
            <div style={{ maxWidth: 1540, margin: '0 auto', padding: '0 20px', boxSizing: 'border-box' }}>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {items.map((item, i) => {
                        const isOpen = activeIndex === i;
                        return (
                            <div key={i} style={{
                                background: bgColor,
                                borderBottom: i < items.length - 1 ? '2px solid #fff' : 'none'
                            }}>
                                <div
                                    onClick={() => setActiveIndex(isOpen ? null : i)}
                                    style={{
                                        padding: '20px 24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 20,
                                        cursor: 'pointer'
                                    }}
                                >
                                    {/* Custom Dot Icon */}
                                    <div style={{
                                        width: 28, height: 28, borderRadius: '50%', background: dotColor,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                        boxShadow: 'inset 0 0 0 5px #fff, inset 0 0 0 8px rgba(0,0,0,0.1)'
                                    }}>
                                        <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#fff' }} />
                                    </div>

                                    <div style={{ fontWeight: 800, fontSize: '1.25rem', color: titleColor, flex: 1, userSelect: 'none' }}>
                                        {item.title}
                                    </div>
                                </div>

                                {/* Expandable Content */}
                                <div style={{
                                    maxHeight: isOpen ? 500 : 0,
                                    overflow: 'hidden',
                                    transition: 'max-height 0.3s ease-in-out',
                                    padding: isOpen ? '0 24px 24px 72px' : '0 24px 0 72px',
                                    opacity: isOpen ? 1 : 0,
                                    transitionProperty: 'max-height, padding, opacity'
                                }}>
                                    <div style={{ fontSize: '1rem', color: descColor, lineHeight: 1.6 }}>
                                        {item.description}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function VerticalBannerModule({ config }) {
    const items = useMemo(() => {
        if (config?.items && Array.isArray(config.items) && config.items.length > 0) {
            return config.items;
        }
        // Fallback for legacy single image
        if (config?.image) {
            return [{ image: config.image, alt: config.alt || 'Banner', link: config.link }];
        }
        return [];
    }, [config]);

    const [currentIndex, setCurrentIndex] = useState(0);
    const effect = config?.effect || 'fade';
    const interval = (Number(config?.speed) || 5) * 1000;

    useEffect(() => {
        if (items.length <= 1) return;
        const timer = setInterval(() => {
            setCurrentIndex(prev => (prev + 1) % items.length);
        }, interval);
        return () => clearInterval(timer);
    }, [items.length, interval]);

    if (items.length === 0) return null;

    const renderItem = (item, index) => {
        const isActive = index === currentIndex;
        const content = (
            <img
                src={resolveAssetUrl(item.image)}
                alt={item.alt || 'Banner'}
                style={{
                    width: '100%',
                    maxHeight: 600,
                    objectFit: 'contain',
                    display: 'block',
                    borderRadius: 8,
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                }}
            />
        );

        const isExternal = item.link?.startsWith('http');
        const Wrap = ({ children }) => {
            if (!item.link) return <div style={{ display: 'block' }}>{children}</div>;
            if (isExternal) return <a href={item.link} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>{children}</a>;
            return <Link to={item.link} style={{ display: 'block' }}>{children}</Link>;
        };

        let style = {
            position: index === 0 ? 'relative' : 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transition: `all 0.8s ease-in-out`,
            opacity: isActive ? 1 : 0,
            zIndex: isActive ? 2 : 1,
            pointerEvents: isActive ? 'auto' : 'none'
        };

        if (effect === 'slide') {
            const offset = (index - currentIndex + items.length) % items.length;
            style = {
                ...style,
                opacity: 1,
                transform: `translateX(${offset === 0 ? '0' : offset === 1 || (offset === -(items.length - 1)) ? '100%' : '-100%'})`,
                zIndex: isActive ? 2 : 1,
                transition: isActive ? 'transform 0.8s ease-in-out' : 'none'
            };
            // Simplest slide for 2+ items: just show active and hide others with transform
            style = {
                position: index === 0 ? 'relative' : 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transition: 'transform 0.8s ease-in-out, opacity 0.8s ease-in-out',
                transform: isActive ? 'translateX(0)' : 'translateX(100%)',
                opacity: isActive ? 1 : 0,
                zIndex: isActive ? 2 : 1,
                pointerEvents: isActive ? 'auto' : 'none'
            };
        }

        return (
            <div key={index} style={style}>
                <Wrap>{content}</Wrap>
            </div>
        );
    };

    return (
        <div style={{ padding: '0 0 20px 0', marginTop: 82, maxWidth: 260, margin: '82px auto 0 auto', position: 'relative', overflow: 'hidden' }}>
            {items.map((item, idx) => renderItem(item, idx))}
        </div>
    );
}

function DyqaniAferTejeModule({ config }) {
    const [shops, setShops] = useState([]);
    const [selectedShop, setSelectedShop] = useState(null);
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const [isMapReady, setIsMapReady] = useState(false);
    const [isDirectHover, setIsDirectHover] = useState(false);
    const markersRef = useRef({});

    useEffect(() => {
        fetch(`${API_BASE_URL}/shop-locations`)
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) {
                    setShops(data);
                }
            })
            .catch(err => console.error('Error fetching shops:', err));
    }, []);

    // Initialize Map
    useEffect(() => {
        if (!mapRef.current || mapInstance.current) return;

        // Default: Albania center
        const defaultLat = 41.15;
        const defaultLng = 20.17;
        const defaultZoom = 7.5;

        mapInstance.current = L.map(mapRef.current, {
            zoomControl: false,
            attributionControl: false
        }).setView([defaultLat, defaultLng], defaultZoom);

        L.tileLayer('https://basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}{r}.png?key=cb1_41m7_3_a23203cc7cb74bb153cfa656', {
            maxZoom: 19
        }).addTo(mapInstance.current);

        L.control.zoom({ position: 'topright' }).addTo(mapInstance.current);
        setIsMapReady(true);

        return () => {
            if (mapInstance.current) {
                mapInstance.current.remove();
                mapInstance.current = null;
                setIsMapReady(false);
            }
        };
    }, []);

    // Helper to get Icon (Simplified to restore visibility)
    const getMarkerIcon = (isActive) => {
        return L.divIcon({
            className: isActive ? 'custom-shop-icon-active' : 'custom-shop-icon',
            html: `
                <div style="width: 24px; height: 34px; position: relative; display: flex; align-items: center; justify-content: center;">
                    <svg width="24" height="34" viewBox="0 0 30 42" xmlns="http://www.w3.org/2000/svg" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));">
                        <path d="M15 0C6.7 0 0 6.7 0 15c0 10 15 27 15 27s15-17 15-27c0-8.3-6.7-15-15-15z" fill="#007da5"/>
                        ${isActive ? 
                            `<circle cx="15" cy="15" r="7" fill="#f48d21" style="animation: pulse-dot 2s infinite ease-in-out;"/>` : 
                            `<circle cx="15" cy="15" r="4.5" fill="#ffffff" opacity="0.3"/>`
                        }
                    </svg>
                </div>
            `,
            iconSize: [24, 34],
            iconAnchor: [12, 34],
            popupAnchor: [0, -32]
        });
    };

    // Bulletproof Marker Management
    useEffect(() => {
        if (!isMapReady || !mapInstance.current || shops.length === 0) return;

        // Ensure map is correctly sized
        mapInstance.current.invalidateSize();

        // Aggressively clear ALL existing markers to prevent disappearance/stale state
        Object.values(markersRef.current).forEach(m => m.remove());
        markersRef.current = {};

        const activeId = selectedShop ? String(selectedShop.id) : null;

        shops.forEach(shop => {
            const sid = String(shop.id);
            const lat = parseFloat(shop.latitude);
            const lng = parseFloat(shop.longitude);
            if (isNaN(lat) || isNaN(lng)) return;

            const isActive = activeId === sid;
            const icon = getMarkerIcon(isActive);

            const marker = L.marker([lat, lng], { 
                icon: icon, 
                zIndexOffset: isActive ? 5000 : 500 
            })
            .addTo(mapInstance.current)
            .bindPopup(`
                <div style="padding: 10px; font-family: Inter, sans-serif; min-width: 150px; text-align: center;">
                    <div style="font-weight: 600; color: #007da5; font-size: 1.05rem;">${shop.name}</div>
                    <div style="color: #64748b; font-size: 0.85rem; margin-top: 4px;">${shop.address}</div>
                </div>
            `, { closeButton: false, className: 'modern-popup' })
            .on('click', () => setSelectedShop(shop));
            
            markersRef.current[sid] = marker;
        });

        if (selectedShop && markersRef.current[activeId]) {
            markersRef.current[activeId].openPopup();
            mapInstance.current.flyTo([parseFloat(selectedShop.latitude), parseFloat(selectedShop.longitude)], 14, { duration: 0.85 });
        }
    }, [isMapReady, shops, selectedShop]);

    return (
        <section style={{ width: '100%', margin: '40px 0' }}>
            <div style={{ maxWidth: 1540, margin: '0 auto', padding: '0 20px', boxSizing: 'border-box' }}>
                {config?.title && (
                    <h2 style={{ fontSize: '2rem', fontWeight: 700, color: config.titleColor || '#003b49', marginBottom: 20 }}>
                        {config.title}
                    </h2>
                )}

                <div style={{ display: 'flex', gap: 24, flexDirection: 'row', flexWrap: 'wrap', marginTop: 24 }}>
                    {/* Compact Modern Sidebar */}
                    <div className="shops-sidebar" style={{ 
                        flex: '1 1 300px', 
                        maxHeight: 700, 
                        overflowY: 'auto', 
                        padding: '8px',
                        background: '#f8fafc',
                        borderRadius: 24,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        boxShadow: 'inset 0 2px 8px 0 rgba(0, 0, 0, 0.05)'
                    }}>
                        {shops.map(shop => (
                            <div
                                key={shop.id}
                                onClick={() => {
                                    setSelectedShop(shop);
                                    if (window.innerWidth < 768 && mapRef.current) {
                                        mapRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    }
                                }}
                                style={{
                                    padding: '16px 20px',
                                    borderRadius: 16,
                                    cursor: 'pointer',
                                    background: selectedShop?.id === shop.id ? '#fff' : 'transparent',
                                    boxShadow: selectedShop?.id === shop.id ? '0 8px 16px -4px rgba(0, 125, 165, 0.15)' : 'none',
                                    border: selectedShop?.id === shop.id ? '2px solid #007da5' : '2px solid transparent',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 14
                                }}
                            >
                                <div style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    background: selectedShop?.id === shop.id ? '#f48d21' : '#cbd5e1',
                                    boxShadow: selectedShop?.id === shop.id ? '0 0 10px rgba(244, 141, 33, 0.5)' : 'none',
                                    flexShrink: 0
                                }} />
                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                    <div style={{ fontWeight: 600, color: selectedShop?.id === shop.id ? '#007da5' : '#111827', fontSize: '1rem', marginBottom: 2, transition: 'color 0.3s' }}>{shop.name}</div>
                                    <div style={{ color: '#64748b', fontSize: '0.85rem', lineHeight: 1.4 }}>{shop.address}</div>
                                </div>
                                {selectedShop?.id === shop.id && (
                                    <div style={{ color: '#f48d21', animation: 'fadeInRight 0.4s ease-out' }}>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                                    </div>
                                )}
                            </div>
                        ))}
                        {shops.length === 0 && <div style={{ padding: '40px', color: '#94a3b8', textAlign: 'center', fontSize: '0.9rem' }}>Duke kërkuar...</div>}
                    </div>

                    {/* Interactive Map Wrapper */}
                    <div style={{ flex: '2.8 1 500px', height: 700, background: '#fff', borderRadius: 32, overflow: 'hidden', position: 'relative', boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.1)' }}>
                        <div ref={mapRef} style={{ width: '100%', height: '100%', zIndex: 1 }} />
                        
                        {selectedShop && (
                            <button
                                onClick={() => {
                                    const url = `https://www.google.com/maps/dir/?api=1&destination=${selectedShop.latitude},${selectedShop.longitude}`;
                                    window.open(url, '_blank');
                                }}
                                style={{
                                    position: 'absolute',
                                    bottom: 30,
                                    right: 30,
                                    zIndex: 1000,
                                    background: isDirectHover ? '#f48d21' : '#007da5',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 16,
                                    padding: '14px 28px',
                                    fontWeight: 800,
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    boxShadow: isDirectHover ? '0 15px 35px rgba(244, 141, 33, 0.5)' : '0 12px 30px rgba(0, 125, 165, 0.4)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    animation: 'fadeInRight 0.5s ease-out',
                                    transform: isDirectHover ? 'translateY(-4px) scale(1.05)' : 'translateY(0) scale(1)'
                                }}
                                onMouseEnter={() => setIsDirectHover(true)}
                                onMouseLeave={() => setIsDirectHover(false)}
                            >
                                <svg 
                                    width="22" 
                                    height="22" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="#fff" 
                                    strokeWidth="3" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                >
                                    <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 3a8 8 0 0 1 8 7.2c0 7.3-8 11.8-8 11.8z"/>
                                    <circle cx="12" cy="10" r="3"/>
                                    <path d="m15 15 3-3-3-3m-9 3h9"/>
                                </svg>
                                Directions
                            </button>
                        )}

                        <style>{`
                            @keyframes pulse-dot {
                                0% { opacity: 0.3; }
                                50% { opacity: 1; }
                                100% { opacity: 0.3; }
                            }
                            @keyframes pulse-ring {
                                0% { transform: scale(0.33); opacity: 0; }
                                33% { transform: scale(1); opacity: 0.6; }
                                100% { transform: scale(1.5); opacity: 0; }
                            }
                            @keyframes fadeInRight {
                                from { opacity: 0; transform: translateX(-15px); }
                                to { opacity: 1; transform: translateX(0); }
                            }
                            /* Leaflet Core Fallback */
                            .leaflet-marker-icon { position: absolute !important; display: block !important; }
                            .leaflet-zoom-animated { transition: transform 0.25s cubic-bezier(0,0,0.25,1); }
                            
                            .leaflet-div-icon {
                                background: transparent !important;
                                border: none !important;
                            }
                            .custom-shop-icon { 
                                transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); 
                            }
                            .custom-shop-icon:hover { 
                                transform: scale(1.1) translateY(-2px); 
                                z-index: 1000 !important;
                            }
                            .leaflet-container { font-family: 'Inter', sans-serif !important; background: #f1f5f9 !important; border: 1px solid #e2e8f0; }
                            .modern-popup .leaflet-popup-content-wrapper {
                                border-radius: 20px;
                                padding: 8px;
                                box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
                                border: 1px solid rgba(255,255,255,0.8);
                                backdrop-filter: blur(8px);
                                background: rgba(255,255,255,0.95);
                            }
                            .modern-popup .leaflet-popup-tip {
                                display: none;
                            }
                            .shops-sidebar::-webkit-scrollbar { width: 8px; }
                            .shops-sidebar::-webkit-scrollbar-track { background: transparent; }
                            .shops-sidebar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 20px; border: 2.5px solid #f8fafc; }
                            .shops-sidebar::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
                        `}</style>
                    </div>
                </div>
            </div>
        </section>
    );
}

function DoubleImageMovementModule({ config }) {
    const {
        imageLeft,
        imageRight,
        linkLeft,
        linkRight,
        splitPercent = 80,
        movingImage = 'both',
        topImage = 'left',
        direction = 'right',
        trigger = 'hover',
        effect = 'slide',
        transitionSpeed = 2, // Default to 2s for a smoother car ride
        autoInterval = 4000
    } = config || {};

    const [isMoved, setIsMoved] = useState(false);
    const containerId = useRef(`dim-${Math.random().toString(36).substr(2, 9)}`).current;
    const sectionRef = useRef(null);
    const [scrollProgress, setScrollProgress] = useState(0);

    // Using refs to store the current target scroll progress and current actual animated progress
    const targetProgressRef = useRef(0);
    const currentProgressRef = useRef(0);
    const rafRef = useRef(null);

    useEffect(() => {
        if (trigger === 'time') {
            const interval = setInterval(() => {
                setIsMoved(prev => !prev);
            }, autoInterval);
            return () => clearInterval(interval);
        } else if (trigger === 'scroll') {
            const handleScroll = () => {
                if (!sectionRef.current) return;
                const rect = sectionRef.current.getBoundingClientRect();
                let progress = 0;
                // rect.top < 0 means the top of the element has hit the top of the viewport and is scrolling past
                if (rect.top < 0) {
                    progress = (-rect.top) / rect.height;
                    progress = Math.max(0, Math.min(1, progress));
                }
                targetProgressRef.current = progress;
            };

            const animateScroll = () => {
                // LERP parameters. 0.1 is smooth, 1.0 is instant.
                const easing = 0.08;
                // Move current slightly towards target
                const diff = targetProgressRef.current - currentProgressRef.current;

                // If the difference is tiny, snap to it. Else, interpolate
                if (Math.abs(diff) > 0.001) {
                    currentProgressRef.current += diff * easing;
                } else {
                    currentProgressRef.current = targetProgressRef.current;
                }

                setScrollProgress(currentProgressRef.current);
                setIsMoved(currentProgressRef.current > 0);

                rafRef.current = requestAnimationFrame(animateScroll);
            };

            window.addEventListener('scroll', handleScroll, { passive: true });

            // Kickoff animation loop
            handleScroll();
            currentProgressRef.current = targetProgressRef.current; // sync initial
            rafRef.current = requestAnimationFrame(animateScroll);

            return () => {
                window.removeEventListener('scroll', handleScroll);
                if (rafRef.current) cancelAnimationFrame(rafRef.current);
            };
        }
    }, [trigger, autoInterval]);

    const handleInteraction = () => {
        if (trigger === 'click') setIsMoved(!isMoved);
    };

    const handleMouseEnter = () => {
        if (trigger === 'hover') setIsMoved(true);
    };

    const handleMouseLeave = () => {
        if (trigger === 'hover') setIsMoved(false);
    };

    const leftPercent = splitPercent || 80;
    const rightPercent = 100 - leftPercent;

    // Use a truly extreme Z-Index and linear-stepped keyframes for "car" movement
    const getAnimation = (side) => {
        if (trigger === 'scroll') return 'none';
        const shouldMove = movingImage === 'both' || movingImage === side;
        if (!shouldMove || !isMoved) return 'none';
        if (effect !== 'slide') return 'none';
        return `${containerId}-move-${side} ${transitionSpeed}s forwards cubic-bezier(0.2, 0, 0.2, 1)`;
    };

    const getImageStyle = (side) => {
        const shouldMove = movingImage === 'both' || movingImage === side;
        let transformStr = 'scale(1)';
        let opacityVal = 1;
        let animationStr = 'none';
        let transitionStr = `transform ${transitionSpeed}s ease, opacity ${transitionSpeed}s ease, box-shadow ${transitionSpeed}s ease`;
        // let shadowStr = '0 10px 30px -10px rgba(0,0,0,0.1)';

        if (trigger === 'scroll') {
            transitionStr = 'none'; // Lock exactly to scroll without smoothing delay
            if (shouldMove) {
                // Determine effect progress directly
                const p = scrollProgress; // 0 when fully visible, 1 when completely scrolled past
                if (effect === 'slide') {
                    const distVal = 120; // Match 120vw
                    let tx = 0, ty = 0;
                    if (direction === 'right') tx = distVal * p;
                    if (direction === 'left') tx = -distVal * p;
                    if (direction === 'bottom') ty = distVal * p;
                    if (direction === 'top') ty = -distVal * p;
                    transformStr = `translate(${tx}vw, ${ty}vw)`;
                } else if (effect === 'zoom') {
                    transformStr = `scale(${1 + 0.15 * p})`;
                } else if (effect === 'fade') {
                    opacityVal = 1 - 0.9 * p;
                }

                // Progressive animation states
            }
        } else {
            if (effect === 'slide') {
                animationStr = getAnimation(side);
            } else if (effect === 'zoom' && isMoved && shouldMove) {
                transformStr = 'scale(1.15)';
            } else if (effect === 'fade' && isMoved && shouldMove) {
                opacityVal = 0.1;
            }
            // if (isMoved && shouldMove) {
            //     shadowStr = '0 50px 100px -20px rgba(0,0,0,0.7)';
            // }
        }

        return {
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: 'cover',
            borderRadius: 20,
            animation: animationStr,
            transform: transformStr,
            opacity: opacityVal,
            transition: transitionStr
            // boxShadow: shadowStr
        };
    };

    const ImageWrapper = ({ link, side, children, style }) => {
        const shouldMove = movingImage === 'both' || movingImage === side;
        const isCurrentlyTop = topImage === side;

        // Ensure standard Z-Index for base view
        const baseZIndex = isCurrentlyTop ? 10 : 5;
        // Elevate to maximum possible integer during movement
        const activeZIndex = 2147483647;

        const wrapperStyle = {
            ...style,
            position: 'relative',
            zIndex: (isMoved && shouldMove) ? activeZIndex : baseZIndex,
            transition: 'z-index 0s',
            overflow: 'visible',
            backgroundColor: 'transparent'
        };

        if (link) {
            return (
                <Link to={link || '#'} style={{ ...wrapperStyle, display: 'block', textDecoration: 'none' }}>
                    {children}
                </Link>
            );
        }
        return <div style={wrapperStyle}>{children}</div>;
    };

    // Calculate distance for "End of Screen"
    // Since images are within a maxWidth container, we need to push them past the residual screen width
    const dist = '120vw';

    const hasLeft = Boolean(imageLeft && String(imageLeft).trim() !== '');
    const hasRight = Boolean(imageRight && String(imageRight).trim() !== '');

    if (!hasLeft && !hasRight) return null;

    return (
        <section
            ref={sectionRef}
            className="double-image-movement-module"
            style={{
                width: '100%',
                margin: '100px 0',
                overflow: 'hidden', // PREVENT HORIZONTAL SCROLL
                position: 'relative',
                zIndex: isMoved ? 99999 : 1
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleInteraction}
        >
            <div style={{
                maxWidth: 1540,
                margin: '0 auto',
                padding: '0 20px',
                display: 'flex',
                flexDirection: 'row',
                gap: (hasLeft && hasRight) ? 20 : 0,
                boxSizing: 'border-box',
                position: 'relative',
                overflow: 'visible' // Allow slide within the hidden section
            }}>
                {/* Left Image */}
                {hasLeft && (
                    <ImageWrapper
                        side="left"
                        link={imageLeft ? linkLeft : null}
                        style={{
                            flex: (hasLeft && hasRight) ? `0 0 calc(${leftPercent}% - 10px)` : '1 1 100%',
                            borderRadius: 20,
                            cursor: trigger === 'click' ? 'pointer' : 'default',
                            aspectRatio: '21/9',
                        }}
                    >
                        <img
                            src={resolveAssetUrl(imageLeft)}
                            alt="Left focus"
                            style={getImageStyle('left')}
                        />
                    </ImageWrapper>
                )}

                {/* Right Image */}
                {hasRight && (
                    <ImageWrapper
                        side="right"
                        link={imageRight ? linkRight : null}
                        style={{
                            flex: (hasLeft && hasRight) ? `0 0 calc(${rightPercent}% - 10px)` : '1 1 100%',
                            borderRadius: 20,
                            cursor: trigger === 'click' ? 'pointer' : 'default',
                            aspectRatio: '21/9',
                        }}
                    >
                        <img
                            src={resolveAssetUrl(imageRight)}
                            alt="Right focus"
                            style={getImageStyle('right')}
                        />
                    </ImageWrapper>
                )}
            </div>

            <style>{`
                @keyframes ${containerId}-move-left {
                    0%   { transform: translate(0, 0); }
                    25%  { transform: translate(${direction === 'right' ? dist : direction === 'left' ? `-${dist}` : '0'}, ${direction === 'bottom' ? dist : direction === 'top' ? `-${dist}` : '0'}); }
                    100% { transform: translate(${direction === 'right' ? dist : direction === 'left' ? `-${dist}` : '0'}, ${direction === 'bottom' ? dist : direction === 'top' ? `-${dist}` : '0'}); }
                }
                @keyframes ${containerId}-move-right {
                    0%   { transform: translate(0, 0); }
                    25%  { transform: translate(${direction === 'right' ? dist : direction === 'left' ? `-${dist}` : '0'}, ${direction === 'bottom' ? dist : direction === 'top' ? `-${dist}` : '0'}); }
                    100% { transform: translate(${direction === 'right' ? dist : direction === 'left' ? `-${dist}` : '0'}, ${direction === 'bottom' ? dist : direction === 'top' ? `-${dist}` : '0'}); }
                }
                .double-image-movement-module img {
                    will-change: transform;
                    pointer-events: auto;
                    position: relative;
                }
                @media (max-width: 1024px) {
                    .double-image-movement-module > div {
                        flex-direction: column !important;
                        gap: 30px !important;
                    }
                    .double-image-movement-module > div > div, 
                    .double-image-movement-module > div > a {
                        flex: 0 0 100% !important;
                        width: 100% !important;
                        aspect-ratio: 16/9 !important;
                    }
                }
            `}</style>
        </section>
    );
}

function BundleOfferModule({ config }) {
    const [products, setProducts] = useState([]);
    const [selectedLeftIds, setSelectedLeftIds] = useState([]);
    const [selectedRightIds, setSelectedRightIds] = useState([]);
    const [searchLeftQuery, setSearchLeftQuery] = useState("");
    const [searchRightQuery, setSearchRightQuery] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            setLoading(true);
            try {
                const limitMode = config.limitMode || 'all';
                let fetchedProducts = [];
                let mainProduct = null;

                if (limitMode === 'single_product' && config.singleProductId) {
                    try {
                        const p = await getProductById(config.singleProductId);
                        if (p && !p.error) {
                            mainProduct = p;
                        }
                    } catch (err) {
                        console.error("Failed to load single product", err);
                    }
                }

                // Query general products
                const queryParams = { limit: 100 };
                if (limitMode === 'category' && Array.isArray(config.categories) && config.categories.length) {
                    queryParams.category = config.categories;
                }

                const res = await searchProducts(queryParams);
                fetchedProducts = Array.isArray(res) ? res : [];

                if (isMounted) {
                    setProducts(fetchedProducts);
                    if (limitMode === 'single_product' && mainProduct) {
                        setSelectedLeftIds([mainProduct.id]);
                    } else if (fetchedProducts.length > 0) {
                        setSelectedLeftIds([fetchedProducts[0].id]);
                    }
                }
            } catch (err) {
                console.error("Failed to load products for bundle-offer", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        load();
        return () => { isMounted = false; };
    }, [config.limitMode, config.categories, config.singleProductId]);

    // Available left products: exclude those selected on the right
    const leftProductsAvailable = useMemo(() => {
        return products.filter(p => !selectedRightIds.includes(p.id));
    }, [products, selectedRightIds]);

    // Available right products: exclude those selected on the left
    const rightProductsAvailable = useMemo(() => {
        return products.filter(p => !selectedLeftIds.includes(p.id));
    }, [products, selectedLeftIds]);

    const toggleLeftProduct = (prod) => {
        if (config.limitMode === 'single_product') return; // locked

        if (prod.stock <= 0) {
            toast.error("Ky produkt nuk është në stok.");
            return;
        }

        setSelectedLeftIds(prev => {
            if (prev.includes(prod.id)) {
                if (prev.length <= 1) {
                    toast.error("Duhet të zgjidhni të paktën një produkt kryesor.");
                    return prev;
                }
                return prev.filter(x => x !== prod.id);
            } else {
                return [...prev, prod.id];
            }
        });
    };

    const toggleRightProduct = (prod) => {
        if (prod.stock <= 0) {
            toast.error("Ky produkt nuk është në stok.");
            return;
        }
        setSelectedRightIds(prev =>
            prev.includes(prod.id) ? prev.filter(x => x !== prod.id) : [...prev, prod.id]
        );
    };

    const leftSelectedProducts = useMemo(() => {
        return products.filter(p => selectedLeftIds.includes(p.id));
    }, [products, selectedLeftIds]);

    const rightSelectedProducts = useMemo(() => {
        return products.filter(p => selectedRightIds.includes(p.id));
    }, [products, selectedRightIds]);

    const leftPrice = useMemo(() => {
        return leftSelectedProducts.reduce((sum, p) => {
            const pPrice = p.salePrice && Number(p.salePrice) > 0 ? Number(p.salePrice) : Number(p.price);
            return sum + pPrice;
        }, 0);
    }, [leftSelectedProducts]);

    const sumRightPrice = useMemo(() => {
        return rightSelectedProducts.reduce((sum, p) => {
            const pPrice = p.salePrice && Number(p.salePrice) > 0 ? Number(p.salePrice) : Number(p.price);
            return sum + pPrice;
        }, 0);
    }, [rightSelectedProducts]);

    const bundlePrice = useMemo(() => {
        return Math.max(leftPrice, sumRightPrice);
    }, [leftPrice, sumRightPrice]);

    const savings = useMemo(() => {
        if (selectedRightIds.length === 0) return 0;
        const totalValue = leftPrice + sumRightPrice;
        return totalValue - bundlePrice;
    }, [leftPrice, sumRightPrice, bundlePrice, selectedRightIds]);

    const handleAddToBag = async () => {
        if (selectedLeftIds.length === 0) {
            toast.error('Ju lutem zgjidhni të paktën një produkt kryesor.');
            return;
        }
        if (selectedRightIds.length === 0) {
            toast.error('Ju lutem zgjidhni të paktën një produkt shtesë.');
            return;
        }

        const primaryLeft = leftSelectedProducts[0];
        const otherLefts = leftSelectedProducts.slice(1);

        const item = {
            id: primaryLeft.id,
            name: primaryLeft.name,
            image: primaryLeft.image,
            price: primaryLeft.salePrice && Number(primaryLeft.salePrice) > 0 ? Number(primaryLeft.salePrice) : Number(primaryLeft.price),
            isBundle: true,
            bundleItems: [
                ...otherLefts.map(rp => ({
                    id: rp.id,
                    name: rp.name,
                    price: rp.salePrice && Number(rp.salePrice) > 0 ? Number(rp.salePrice) : Number(rp.price),
                    sku: rp.sku,
                    image: rp.image,
                    side: 'left'
                })),
                ...rightSelectedProducts.map(rp => ({
                    id: rp.id,
                    name: rp.name,
                    price: rp.salePrice && Number(rp.salePrice) > 0 ? Number(rp.salePrice) : Number(rp.price),
                    sku: rp.sku,
                    image: rp.image,
                    side: 'right'
                }))
            ]
        };

        try {
            await addCartItem(item, 1);
            toast.success('Pakoja u shtua në shportë me sukses!');
        } catch (err) {
            console.error(err);
            toast.error(err.message || 'Dështoi shtimi i pakos.');
        }
    };

    const filteredLeftList = useMemo(() => {
        if (!searchLeftQuery) return leftProductsAvailable;
        return leftProductsAvailable.filter(p => p.name.toLowerCase().includes(searchLeftQuery.toLowerCase()));
    }, [leftProductsAvailable, searchLeftQuery]);

    const filteredRightList = useMemo(() => {
        if (!searchRightQuery) return rightProductsAvailable;
        return rightProductsAvailable.filter(p => p.name.toLowerCase().includes(searchRightQuery.toLowerCase()));
    }, [rightProductsAvailable, searchRightQuery]);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400, background: '#f8fafc' }}>
                <div style={{
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #003b49',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    animation: 'spin 1s linear infinite'
                }} />
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    if (products.length === 0) {
        return (
            <div style={{ padding: '40px 20px', textAlign: 'center', background: '#f9f9f9', borderRadius: 8 }}>
                <p style={{ color: '#666' }}>Nuk u gjet asnjë produkt për këtë ofertë.</p>
            </div>
        );
    }

    const titleColor = config.titleColor || '#003b49';

    return (
        <section style={{ width: '100%', padding: '60px 0', background: '#f8fafc', fontFamily: 'Outfit, sans-serif' }}>
            <style>{`
                .bundle-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 20px;
                    padding: 24px;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .bundle-select-item {
                    display: flex;
                    align-items: center;
                    padding: 12px;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: 1.5px solid transparent;
                    margin-bottom: 8px;
                    background: #ffffff;
                }
                .bundle-select-item:hover:not(.disabled) {
                    background: #f1f5f9;
                    border-color: #cbd5e1;
                }
                .bundle-select-item.selected {
                    background: #f0f9ff;
                    border-color: #0084a5;
                }
                .bundle-select-item.disabled {
                    opacity: 0.55;
                    cursor: not-allowed;
                    background: #f8fafc;
                }
                .bundle-checkbox {
                    width: 22px;
                    height: 22px;
                    border-radius: 6px;
                    border: 2px solid #cbd5e1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-right: 12px;
                    background: #fff;
                    transition: all 0.2s ease;
                    flex-shrink: 0;
                }
                .bundle-checkbox.checked {
                    background: #0084a5;
                    border-color: #0084a5;
                }
                .bundle-checkbox.checked::after {
                    content: "✓";
                    color: white;
                    font-size: 14px;
                    font-weight: bold;
                }
                .bundle-scroll-container::-webkit-scrollbar {
                    width: 6px;
                }
                .bundle-scroll-container::-webkit-scrollbar-track {
                    background: transparent;
                }
                .bundle-scroll-container::-webkit-scrollbar-thumb {
                    background-color: #cbd5e1;
                    border-radius: 3px;
                }
                .bundle-btn {
                    width: 100%;
                    padding: 16px;
                    background: #f97316;
                    color: white;
                    border: none;
                    border-radius: 14px;
                    font-size: 16px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    box-shadow: 0 4px 14px 0 rgba(249, 115, 22, 0.3);
                }
                .bundle-btn:hover:not(:disabled) {
                    background: #ea580c;
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px 0 rgba(249, 115, 22, 0.4);
                }
                .bundle-btn:disabled {
                    background: #cbd5e1;
                    box-shadow: none;
                    cursor: not-allowed;
                }
            `}</style>

            <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
                <h2 style={{
                    fontSize: '2.5rem',
                    fontWeight: 800,
                    color: titleColor,
                    textAlign: 'center',
                    marginBottom: '8px',
                    letterSpacing: '-0.02em'
                }}>
                    {config.title || 'Oferta Bundle'}
                </h2>
                <p style={{
                    fontSize: '1rem',
                    color: '#64748b',
                    textAlign: 'center',
                    marginBottom: '40px'
                }}>
                    Kombinoni produktet kryesore me shtesat dhe përfitoni çmime ekskluzive!
                </p>

                {/* Two Column Grid */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1.1fr',
                    gap: '40px',
                    alignItems: 'start'
                }}>
                    
                    {/* Left Column: Main Product(s) */}
                    <div className="bundle-card" style={{ display: 'flex', flexDirection: 'column', maxHeight: '520px' }}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1e293b', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', background: '#003b49', color: '#fff', borderRadius: '50%', fontSize: '14px' }}>1</span>
                            Produkti Kryesor
                        </h3>

                        {config.limitMode === 'single_product' ? (
                            // Preselected locked display
                            leftSelectedProducts[0] && (
                                <div style={{ border: '1.5px solid #e2e8f0', borderRadius: '16px', padding: '20px', background: '#fff', position: 'relative', overflow: 'hidden' }}>
                                    <div style={{ position: 'absolute', top: '12px', right: '12px', background: '#e2e8f0', color: '#475569', fontSize: '11px', fontWeight: 600, padding: '4px 8px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                                        I kyçur
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                                        <img src={resolveAssetUrl(leftSelectedProducts[0].image)} alt={leftSelectedProducts[0].name} style={{ maxHeight: '180px', maxWidth: '100%', objectFit: 'contain' }} />
                                    </div>
                                    <h4 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#0f172a', marginBottom: '8px' }}>{leftSelectedProducts[0].name}</h4>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                                        <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0084a5' }}>
                                            {leftPrice.toFixed(2)} €
                                        </span>
                                        {leftSelectedProducts[0].salePrice && Number(leftSelectedProducts[0].salePrice) > 0 && (
                                            <span style={{ fontSize: '0.9rem', color: '#94a3b8', textDecoration: 'line-through' }}>
                                                {Number(leftSelectedProducts[0].price).toFixed(2)} €
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )
                        ) : (
                            // Multiple Checkbox Selector for Left Column
                            <>
                                <div style={{ position: 'relative', marginBottom: '16px' }}>
                                    <input
                                        type="text"
                                        placeholder="Kërko produktin kryesor..."
                                        value={searchLeftQuery}
                                        onChange={(e) => setSearchLeftQuery(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            border: '1.5px solid #e2e8f0',
                                            borderRadius: '12px',
                                            outline: 'none',
                                            fontSize: '14px',
                                            transition: 'all 0.2s'
                                        }}
                                        onFocus={e => e.target.style.borderColor = '#0084a5'}
                                        onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                                    />
                                </div>

                                <div className="bundle-scroll-container" style={{ overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
                                    {filteredLeftList.map(p => {
                                        const pPrice = p.salePrice && Number(p.salePrice) > 0 ? Number(p.salePrice) : Number(p.price);
                                        const isSelected = selectedLeftIds.includes(p.id);
                                        const isOutOfStock = Number(p.stock || 0) <= 0;
                                        return (
                                            <div
                                                key={p.id}
                                                className={`bundle-select-item ${isSelected ? 'selected' : ''} ${isOutOfStock ? 'disabled' : ''}`}
                                                onClick={() => !isOutOfStock && toggleLeftProduct(p)}
                                            >
                                                <div className={`bundle-checkbox ${isSelected ? 'checked' : ''}`} />
                                                <img src={resolveAssetUrl(p.image)} alt="" style={{ width: '48px', height: '48px', objectFit: 'contain', marginRight: '16px', background: '#f8fafc', borderRadius: '8px', padding: '2px', flexShrink: 0 }} />
                                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                                        <span style={{ fontSize: '14px', fontWeight: 700, color: '#0084a5' }}>{pPrice.toFixed(2)} €</span>
                                                        {isOutOfStock ? (
                                                            <span style={{ color: '#ef4444', fontSize: '11px', fontWeight: 600, background: '#fef2f2', padding: '2px 6px', borderRadius: '4px' }}>Jashtë Stokut</span>
                                                        ) : (
                                                            <span style={{ color: '#10b981', fontSize: '11px', fontWeight: 600, background: '#ecfdf5', padding: '2px 6px', borderRadius: '4px' }}>Në Stok</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {filteredLeftList.length === 0 && (
                                        <div style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>Nuk u gjet asnjë produkt kryesor.</div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Right Column: Add-ons and Summary */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                        
                        {/* Add-ons Selector */}
                        <div className="bundle-card" style={{ display: 'flex', flexDirection: 'column', maxHeight: '520px' }}>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', background: '#003b49', color: '#fff', borderRadius: '50%', fontSize: '14px' }}>2</span>
                                Zgjidhni shtesat e bundle-it
                            </h3>

                            <div style={{ position: 'relative', marginBottom: '16px' }}>
                                <input
                                    type="text"
                                    placeholder="Kërko produkte shtesë..."
                                    value={searchRightQuery}
                                    onChange={(e) => setSearchRightQuery(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        border: '1.5px solid #e2e8f0',
                                        borderRadius: '12px',
                                        outline: 'none',
                                        fontSize: '14px',
                                        transition: 'all 0.2s'
                                    }}
                                    onFocus={e => e.target.style.borderColor = '#0084a5'}
                                    onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                                />
                            </div>

                            <div className="bundle-scroll-container" style={{ overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
                                {filteredRightList.map(p => {
                                    const pPrice = p.salePrice && Number(p.salePrice) > 0 ? Number(p.salePrice) : Number(p.price);
                                    const isSelected = selectedRightIds.includes(p.id);
                                    const isOutOfStock = Number(p.stock || 0) <= 0;
                                    return (
                                        <div
                                            key={p.id}
                                            className={`bundle-select-item ${isSelected ? 'selected' : ''} ${isOutOfStock ? 'disabled' : ''}`}
                                            onClick={() => !isOutOfStock && toggleRightProduct(p)}
                                        >
                                            <div className={`bundle-checkbox ${isSelected ? 'checked' : ''}`} />
                                            <img src={resolveAssetUrl(p.image)} alt="" style={{ width: '48px', height: '48px', objectFit: 'contain', marginRight: '16px', background: '#f8fafc', borderRadius: '8px', padding: '2px', flexShrink: 0 }} />
                                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#0084a5' }}>{pPrice.toFixed(2)} €</span>
                                                    {isOutOfStock ? (
                                                        <span style={{ color: '#ef4444', fontSize: '11px', fontWeight: 600, background: '#fef2f2', padding: '2px 6px', borderRadius: '4px' }}>Jashtë Stokut</span>
                                                    ) : (
                                                        <span style={{ color: '#10b981', fontSize: '11px', fontWeight: 600, background: '#ecfdf5', padding: '2px 6px', borderRadius: '4px' }}>Në Stok</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {filteredRightList.length === 0 && (
                                    <div style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>Nuk u gjet asnjë produkt shtesë.</div>
                                )}
                            </div>
                        </div>

                        {/* Deal Summary Panel (Glassmorphism) */}
                        <div style={{
                            background: 'rgba(255, 255, 255, 0.75)',
                            backdropFilter: 'blur(12px)',
                            WebkitBackdropFilter: 'blur(12px)',
                            border: '1px solid rgba(255, 255, 255, 0.5)',
                            borderRadius: '24px',
                            padding: '28px',
                            boxShadow: '0 10px 30px -5px rgba(0, 59, 73, 0.05), 0 4px 12px -2px rgba(0, 59, 73, 0.02)'
                        }}>
                            <h4 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#003b49', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Përmbledhja e Ofertës
                            </h4>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', borderBottom: '1px solid rgba(0, 59, 73, 0.1)', paddingBottom: '20px', marginBottom: '20px' }}>
                                {leftSelectedProducts.map((p, idx) => {
                                    const pPrice = p.salePrice && Number(p.salePrice) > 0 ? Number(p.salePrice) : Number(p.price);
                                    return (
                                        <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', gap: '10px' }}>
                                            <span style={{ color: '#475569', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {idx === 0 ? '1 x ' : '+ '} {p.name}
                                            </span>
                                            <span style={{ fontWeight: 600, color: '#0f172a', flexShrink: 0 }}>{pPrice.toFixed(2)} €</span>
                                        </div>
                                    );
                                })}

                                {rightSelectedProducts.map(p => {
                                    const pPrice = p.salePrice && Number(p.salePrice) > 0 ? Number(p.salePrice) : Number(p.price);
                                    return (
                                        <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', paddingLeft: '12px', gap: '10px' }}>
                                            <span style={{ color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>+ {p.name} (Shtesë)</span>
                                            <span style={{ fontWeight: 500, color: '#475569', flexShrink: 0 }}>{pPrice.toFixed(2)} €</span>
                                        </div>
                                    );
                                })}

                                {selectedRightIds.length === 0 && (
                                    <div style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>
                                        Shto produkte shtesë për të parë ofertën...
                                    </div>
                                )}
                            </div>

                            {/* Savings display */}
                            {savings > 0 && (
                                <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '12px', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ color: '#065f46', fontWeight: 700, fontSize: '14px' }}>Kursim Ekskluziv!</span>
                                        <span style={{ color: '#047857', fontSize: '12px', marginTop: '2px' }}>
                                            {sumRightPrice < leftPrice 
                                                ? 'Të gjitha produktet shtesë janë FALAS!' 
                                                : 'Produktet kryesore janë plotësisht FALAS!'}
                                        </span>
                                    </div>
                                    <span style={{ color: '#10b981', fontWeight: 800, fontSize: '1.2rem', flexShrink: 0 }}>
                                        -{savings.toFixed(2)} €
                                    </span>
                                </div>
                            )}

                            {/* Totals & Button */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '24px' }}>
                                <span style={{ fontSize: '15px', fontWeight: 600, color: '#334155' }}>Çmimi i Paketës:</span>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                    <span style={{ fontSize: '2rem', fontWeight: 800, color: '#003b49', lineHeight: 1 }}>
                                        {bundlePrice.toFixed(2)} €
                                    </span>
                                    {savings > 0 && (
                                        <span style={{ fontSize: '12px', color: '#94a3b8', textDecoration: 'line-through', marginTop: '4px' }}>
                                            {(leftPrice + sumRightPrice).toFixed(2)} €
                                        </span>
                                    )}
                                </div>
                            </div>

                            <button
                                className="bundle-btn"
                                onClick={handleAddToBag}
                                disabled={selectedRightIds.length === 0}
                            >
                                Shto Pakon në Shportë
                            </button>
                        </div>

                    </div>

                </div>
            </div>
        </section>
    );
}

export default function PageRenderer({ content, pageType }) {
    const modules = useMemo(() => {
        if (Array.isArray(content)) {
            // Detect legacy slider: array of slides instead of array of modules
            if (content.length > 0 && !content[0].type && pageType === 'slider') {
                return [{ id: 'legacy-slider', type: 'slider', config: content }];
            }
            return content.filter(m => !(m && m.hidden));
        }

        if (content && typeof content === 'object') {
            // Case 1: Legacy Products Grid
            if (pageType === 'products' || content.mode) {
                return [{
                    id: 'legacy-products',
                    type: 'products',
                    config: {
                        mode: content.mode || 'category',
                        categories: content.category ? [content.category] : [],
                        items: content.items || [],
                        limit: content.limit || 12,
                        title: ''
                    }
                }];
            }
            // Case 2: Legacy Blog List
            if (pageType === 'blogs') {
                return [{
                    id: 'legacy-blogs',
                    type: 'blogs',
                    config: {
                        mode: 'category',
                        limit: content.limit || 8,
                        title: ''
                    }
                }];
            }
        }

        return [];
    }, [content, pageType]);

    // Data fetching for modules that require it (products, blogs)
    // ... rest of the component state/effects ...
    const [allProducts, setAllProducts] = useState([]);
    const [moduleProducts, setModuleProducts] = useState({});
    const [allCategories, setAllCategories] = useState([]);
    const [allPosts, setAllPosts] = useState([]);
    const [allBrands, setAllBrands] = useState([]);

    useEffect(() => {
        const needsBrands = modules.some(m => m.type === 'brands');
        const needsProducts = modules.some(m => m.type === 'products' || m.type === 'offer-grid');
        const needsCategories = modules.some(m => m.type === 'categories');
        const needsBlogs = modules.some(m => m.type === 'blogs');

        if (needsProducts || needsCategories) {
            searchProducts({ limit: 40 }).then(res => {
                const products = Array.isArray(res) ? res : [];
                setAllProducts(products);
            }).catch(err => console.error(err));
        }

        if (needsBrands) {
            getBrandsWithImages().then(res => {
                setAllBrands(Array.isArray(res) ? res : []);
            }).catch(err => console.error(err));
        }
        if (needsCategories) {
            import('../services/categoryService').then(({ getCategories }) => {
                getCategories().then(res => setAllCategories(Array.isArray(res) ? res : [])).catch(err => console.error(err));
            });
        }
        if (needsBlogs) {
            import('../services/blogService').then(({ getPosts }) => {
                getPosts().then(res => setAllPosts(Array.isArray(res) ? res : [])).catch(err => console.error(err));
            });
        }
    }, [modules]);

    useEffect(() => {
        let mounted = true;
        const loadModuleProducts = async () => {
            const productModules = modules.filter(m => m.type === 'products' || m.type === 'offer-grid');
            if (!productModules.length) {
                if (mounted) setModuleProducts({});
                return;
            }
            const next = {};
            for (const mod of productModules) {
                const cfg = mod.config || {};
                let list = [];

                if (cfg.mode === 'manual' && Array.isArray(cfg.items) && cfg.items.length) {
                    const ids = cfg.items.filter(Boolean);
                    for (const id of ids) {
                        try {
                            const p = await getProductById(id);
                            if (p && !p.error) list.push(p);
                        } catch (_) { }
                    }
                } else {
                    const params = {};
                    if (mod.type === 'offer-grid') {
                        params.offer = 'true';
                    }
                    if (cfg.mode === 'offer') params.offer = 'true';
                    if (cfg.mode === 'category') {
                        const cats = Array.isArray(cfg.categories) ? cfg.categories.filter(Boolean) : [];
                        if (cats.length) params.category = cats;
                        else if (cfg.category) params.category = cfg.category;
                    }

                    if (cfg.filters && typeof cfg.filters === 'object') {
                        const built = filterService.buildParams(cfg.filters);
                        Object.assign(params, built);
                    }

                    try {
                        const res = await getFilteredProducts(params);
                        list = Array.isArray(res) ? res : [];
                    } catch (_) {
                        list = [];
                    }
                }

                if (cfg.limit) list = list.slice(0, Number(cfg.limit) || list.length);
                next[mod.id || String(modules.indexOf(mod))] = list;
            }

            if (mounted) setModuleProducts(next);
        };

        loadModuleProducts();
        return () => { mounted = false; };
    }, [modules]);

    if (!modules || modules.length === 0) {
        return (
            <div style={{ padding: '40px 20px', textAlign: 'center', background: '#f9f9f9', borderRadius: 8, border: '1px dashed #ccc' }}>
                <p style={{ color: '#666', fontSize: 16 }}>This page has no content yet or the format is unrecognized.</p>
                <div style={{ marginTop: 12, fontSize: 12, color: '#999' }}>
                    Page Type: {pageType || 'N/A'} | Content Format: {typeof content === 'object' ? (Array.isArray(content) ? 'Array' : 'Object') : typeof content}
                </div>
            </div>
        );
    }

    return (
        <div className="page-renderer-container" style={{ width: '100%' }}>
            {modules.map((mod, i) => {
                let comp = null;
                switch (mod.type) {
                    case 'slider':
                        comp = <SliderModule slides={mod.config} />;
                        break;
                    case 'offer-sliders':
                        comp = <OfferSlidersModule config={mod.config} />;
                        break;
                    case 'split-banner':
                        comp = <SplitBannerModule left={mod.config.left || {}} right={mod.config.right || {}} config={mod.config} />;
                        break;
                    case 'promo-strip':
                        comp = <PromoStripModule config={mod.config} />;
                        break;
                    case 'popup':
                        comp = <PopupModule config={mod.config} />;
                        break;
                    case 'head-tags':
                        comp = <HeadTagsModule config={mod.config} />;
                        break;
                    case 'text':
                        comp = <TextModule text={mod.config.text} />;
                        break;
                    case 'html':
                        comp = <HtmlModule html={mod.config.html} />;
                        break;
                    case 'headline':
                        comp = <HeadlineModule config={mod.config} />;
                        break;
                    case 'image':
                        comp = <ImageModule url={mod.config.url} alt={mod.config.alt} width={mod.config.width} height={mod.config.height} />;
                        break;
                    case 'image-text-button':
                        comp = <ImageTextButtonModule config={mod.config} />;
                        break;
                    case 'button':
                        comp = <ButtonModule config={mod.config} />;
                        break;
                    case 'divider':
                        comp = <DividerModule config={mod.config} />;
                        break;
                    case 'video':
                        comp = <VideoModule config={mod.config} />;
                        break;
                    case 'spacer':
                        comp = <SpacerModule config={mod.config} />;
                        break;
                    case 'products':
                        const moduleKey = mod.id || String(i);
                        const hasModuleData = Object.prototype.hasOwnProperty.call(moduleProducts, moduleKey);
                        const needsFiltered = !!(mod.config && ((mod.config.filters && Object.keys(mod.config.filters).length) || mod.config.mode === 'manual' || mod.config.mode === 'offer' || (Array.isArray(mod.config.categories) && mod.config.categories.length) || mod.config.category));
                        const displayProducts = hasModuleData ? moduleProducts[moduleKey] : (needsFiltered ? [] : allProducts);
                        comp = <ProductsModule config={mod.config} products={displayProducts} />;
                        break;
                    case 'categories':
                        let displayCats = allCategories;
                        if (mod.config.selected && mod.config.selected.length) displayCats = displayCats.filter(c => mod.config.selected.includes(c.name));
                        comp = <CategoriesModule config={mod.config} categories={displayCats} />;
                        break;
                    case 'feature-promo':
                        comp = <FeaturePromoModule config={mod.config} />;
                        break;
                    case 'feature-promo-reverse':
                        comp = <FeaturePromoReverseModule config={mod.config} />;
                        break;
                    case 'blogs':
                        let displayPosts = allPosts;
                        if (mod.config.limit) displayPosts = displayPosts.slice(0, mod.config.limit);
                        comp = <BlogsModule config={mod.config} posts={displayPosts} />;
                        break;
                    case 'brands':
                        comp = <BrandsModule config={mod.config} brands={allBrands} />;
                        break;
                    case 'announcement':
                        comp = <AnnouncementModule config={mod.config} />;
                        break;
                    case 'contact-form':
                        comp = <ContactFormModule config={mod.config} />;
                        break;
                    case 'offer-hero':
                        comp = <OfferHeroModule config={mod.config} />;
                        break;
                    case 'offer-featured':
                        comp = <OfferFeaturedModule config={mod.config} />;
                        break;
                    case 'offer-catalogs':
                        comp = <OfferCatalogsModule config={mod.config} />;
                        break;
                    case 'offer-needs':
                        comp = <OfferNeedsModule config={mod.config} />;
                        break;
                    case 'offer-footer':
                        comp = <OfferFooterModule config={mod.config} />;
                        break;
                    case 'home-oferta':
                        comp = <HomeOfertaModule config={mod.config} />;
                        break;
                    case 'oferta':
                        comp = <OfertaModule config={mod.config} />;
                        break;
                    case 'hr-slider':
                        comp = <HrSliderModule config={mod.config} />;
                        break;
                    case 'metodat-pagese':
                        comp = <MetodatPageseModule config={mod.config} />;
                        break;
                    case 'vertical-banner':
                        comp = <VerticalBannerModule config={mod.config} />;
                        break;
                    case 'dyqani-afer-teje':
                        comp = <DyqaniAferTejeModule config={mod.config} />;
                        break;
                    case 'double-image-movement':
                        comp = <DoubleImageMovementModule config={mod.config} />;
                        break;
                    case 'bundle-offer':
                        comp = <BundleOfferModule config={mod.config} />;
                        break;
                    case 'old-to-new-offer':
                        comp = <OldToNewOfferModule config={mod.config || {}} />;
                        break;
                    default:
                        comp = null;
                        break;
                }
                if (!comp) return null;
                return (
                    <div 
                        key={mod.id || i} 
                        className="page-renderer-module"
                        style={{ animationDelay: `${Math.min(i * 0.08, 0.48)}s`, width: '100%' }}
                    >
                        {comp}
                    </div>
                );
            })}
        </div>
    );
}
